<template>
  <div class="w-full">
    <breadcrumbs-component :breadCrumbs="dashboardBreadCrumbs" />

    <div class="w-full mx-auto px-3 sm:px-4 pb-8 text-base sm:text-lg">
      <!-- Loading Skeleton -->
      <div v-if="loading" class="space-y-4 pt-2">
        <div class="flex gap-2">
          <Skeleton
            v-for="i in 3"
            :key="i"
            width="120px"
            height="44px"
            borderRadius="8px"
          />
        </div>
        <Skeleton height="360px" borderRadius="12px" />
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Skeleton
            v-for="i in 4"
            :key="i"
            height="80px"
            borderRadius="8px"
          />
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton height="160px" borderRadius="8px" />
          <Skeleton height="160px" borderRadius="8px" />
        </div>
      </div>

      <!-- No Results Message -->
      <div
        v-else-if="!latestAssessments || latestAssessments.length === 0"
        class="flex items-center justify-center py-24"
      >
        <div class="text-center p-8">
          <i
            class="pi pi-chart-bar text-5xl text-gray-300 mb-4 block"
          ></i>
          <h2 class="text-gray-700 mb-2 text-lg sm:text-xl font-medium">
            {{ $t('MyResultsDashboardComponent.noAssessmentResults') }}
          </h2>
          <p class="text-gray-500 text-sm sm:text-base">
            {{ $t('MyResultsDashboardComponent.noAssessmentsCompleted') }}
          </p>
        </div>
      </div>

      <template v-else>
        <!-- Assessment selector (same pattern as TeamResultDashboardComponent) -->
        <div
          v-if="latestAssessments?.length"
          class="mb-6 rounded-lg p-4"
        >
          <div class="flex flex-wrap items-center gap-4">
            <h3 class="text-base sm:text-lg font-medium text-gray-700">
              {{ $t('TeamResultDashboardComponent.selectAssessment') }}:
            </h3>
            <div class="relative inline-block max-w-full">
              <select
                :value="selectedTab"
                class="appearance-none bg-white border border-gray-300 rounded-lg px-4
                  py-2 pr-8 text-base sm:text-lg focus:outline-none focus:ring-2
                  focus:ring-orange-500 focus:border-transparent max-w-full min-w-0
                  [field-sizing:content] w-max"
                @change="changeTab($event.target.value)"
              >
                <option
                  v-for="tab in latestAssessments"
                  :key="tab.assessmentId"
                  :value="tab.assessmentId"
                >
                  {{ getAssessmentName(tab.assessmentName) }}
                </option>
              </select>
              <div
                class="absolute inset-y-0 right-0 flex items-center pr-2
                  pointer-events-none"
              >
                <svg
                  class="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <template
          v-for="tab in latestAssessments"
          :key="tab.assessmentId"
        >
          <div v-if="selectedTab === tab.assessmentId" class="!p-0">
              <!-- Error State -->
              <div
                v-if="error"
                class="bg-white rounded-lg shadow-sm p-8
                  text-center text-red-500"
              >
                <i
                  class="pi pi-exclamation-triangle text-3xl mb-2 block"
                ></i>
                <p>{{ error }}</p>
              </div>

              <!-- Missing Chart Config -->
              <div
                v-else-if="!tab.chartConfigs || tab.chartConfigs.length === 0"
                class="bg-white rounded-lg shadow-sm p-8 text-center"
              >
                <i
                  class="pi pi-cog text-3xl text-gray-400 mb-2 block"
                ></i>
                <h3 class="text-gray-700 mb-2 text-lg sm:text-xl font-medium">
                  {{ $t('MyResultsDashboardComponent.chartConfigurationMissing') }}
                </h3>
                <p class="text-gray-500 text-sm sm:text-base">
                  {{ $t('MyResultsDashboardComponent.contactPlatformManager') }}
                </p>
              </div>

              <!-- ===== MAIN CONTENT ===== -->
              <div v-else class="space-y-5">

                <!-- SECTION 1: Header + Score at-a-glance -->
                <div
                  class="bg-white rounded-lg shadow-sm p-4 sm:p-5"
                >
                  <div
                    class="flex flex-col sm:flex-row sm:items-center
                      justify-between gap-2 mb-3"
                  >
                    <div>
                      <h1
                        class="text-lg sm:text-xl font-semibold text-gray-900"
                      >
                        <!-- eslint-disable-next-line max-len -->
                        {{ $t('MyResultsDashboardComponent.assessmentResults',
                         { assessmentType: getAssessmentName(tab.assessmentName) }) }}
                      </h1>
                      <span class="text-sm sm:text-base text-gray-500">
                        {{ $t('MyResultsDashboardComponent.submitted') }}:
                        {{ formatResultsDate(tab.submittedAt) }}
                      </span>
                    </div>
                    <!-- Score badge -->
                    <div class="flex flex-col gap-2 items-end">
                      <div><p class="text-[11px] text-gray-400 font-semibold uppercase tracking-widest">
                        {{ $t('MyResultsDashboardComponent.overallMean') }}
                      </p></div>
                      <div
                        class="flex items-baseline gap-2 rounded-lg">
                        <span
                          class="text-2xl sm:text-3xl font-bold text-gray-900"
                        >{{ formatOwnResultNumber(getOverallAverage(tab)) }}</span>
                        <span class="text-sm sm:text-base text-gray-400">/5.00</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- SECTION 2: Chart (the main visual) -->
                <div class="bg-white rounded-lg shadow-sm p-4 sm:p-5">
                  <!-- Charts (stacked; multiple per schema when configured) -->
                  <div class="space-y-8 w-full">
                    <div
                      v-for="(chartCfg, chartIdx) in tab.chartConfigs"
                      :key="chartCfg._id || chartIdx"
                      class="w-full"
                    >
                      <!-- Comparison controls: one block per bar/boxplot chart; shared selection -->
                      <ResultsComparisonSection
                        v-if="showComparisonForChart(tab, chartCfg)"
                        class="mb-4 sm:mb-5"
                        :currentAssessmentId="tab.assessmentId"
                        :comparison1="
                          selectedComparison1[tab.assessmentId] || ''"
                        :comparison2="
                          selectedComparison2[tab.assessmentId] || ''"
                        :sameTypeAssessments="
                          groupedAssessments[tab.assessmentType] || []"
                        :externalEvaluations="
                          comparisonExternalEvaluationsForTab(tab)"
                        :isExternalEvaluationTab="
                          checkIsExternalEvaluationType(tab.assessmentType)"
                        :deltas="getComparisonDeltas(tab)"
                        :getAssessmentName="getAssessmentName"
                        @update:comparison1="
                          onComparison1Update(tab, $event)"
                        @update:comparison2="
                          onComparison2Update(tab, $event)"
                        @reset="resetComparison(tab.assessmentId)"
                      />
                      <!-- Time controls: one block per line chart; shared filters -->
                      <div
                        v-if="isLineChart(chartCfg)"
                        class="mb-4 flex flex-col sm:flex-row
                          items-start sm:items-center
                          justify-center gap-3 sm:gap-6"
                      >
                        <div class="flex items-center gap-2">
                          <span class="text-base sm:text-lg font-medium text-gray-700">
                            {{ $t('TimePeriodSelector.timePeriod')
                              || 'Time Period' }}:
                          </span>
                          <Select
                            :modelValue="selectedTimePeriod"
                            @update:modelValue="
                              (v) => onTimePeriodChange(v)"
                            :options="timePeriodOptions"
                            optionLabel="label"
                            optionValue="value"
                            class="w-fit max-w-full min-w-0"
                          />
                        </div>
                        <div class="flex items-center gap-2">
                          <span class="text-base sm:text-lg font-medium text-gray-700">
                            {{ $t(
                              'MyResultsDashboardComponent.timeGranularity'
                            ) || 'Interval' }}:
                          </span>
                          <Select
                            v-model="selectedTimeGranularity"
                            :options="granularityOptions"
                            optionLabel="label"
                            optionValue="value"
                            @change="onTimeGranularityChange"
                            class="w-fit max-w-full min-w-0"
                          />
                        </div>
                      </div>

                      <div
                        v-if="isLineChart(chartCfg)
                          && selectedTimePeriod === 'custom'"
                        class="mb-4 flex flex-col sm:flex-row items-center
                          justify-center gap-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div class="flex items-center gap-2">
                          <label class="text-base sm:text-lg font-medium text-gray-700">
                            {{ $t('TimePeriodSelector.fromDate')
                              || 'From:' }}
                          </label>
                          <input
                            v-model="customStartDate"
                            type="date"
                            class="px-2 py-1.5 border border-gray-300
                              rounded-md text-sm sm:text-base focus:outline-none
                              focus:ring-2 focus:ring-orange-500
                              focus:border-orange-500"
                            @change="onCustomDateChange"
                          />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="text-base sm:text-lg font-medium text-gray-700">
                            {{ $t('TimePeriodSelector.toDate') || 'To:' }}
                          </label>
                          <input
                            v-model="customEndDate"
                            type="date"
                            class="px-2 py-1.5 border border-gray-300
                              rounded-md text-sm sm:text-base focus:outline-none
                              focus:ring-2 focus:ring-orange-500
                              focus:border-orange-500"
                            @change="onCustomDateChange"
                          />
                        </div>
                        <button
                          @click="applyCustomRange"
                          class="px-3 py-1.5 bg-orange-500 text-white
                            rounded-md text-sm sm:text-base font-medium
                            hover:bg-orange-600 transition-colors"
                        >
                          {{ $t('TimePeriodSelector.apply') || 'Apply' }}
                        </button>
                      </div>

                      <p
                        v-if="getChartDescription(chartCfg)"
                        class="text-sm sm:text-base text-gray-600 mb-3 text-center"
                      >
                        {{ getChartDescription(chartCfg) }}
                      </p>
                      <div
                        class="w-full"
                        :class="chartHeightClass(chartCfg)"
                      >
                        <template v-if="shouldShowChartVisualization(tab, chartCfg)">
                          <BarChartComponent
                            v-if="isBarChart(chartCfg)"
                            :chart-config="chartCfg"
                            :dimensions="dimensions"
                          />
                          <LineChartComponent
                            v-else-if="isLineChart(chartCfg)"
                            :chart-config="chartCfg"
                            :dimensions="dimensions"
                            :selected-label="selectedTimeBucket"
                            :sticky-answer-counts="stickyAnswerCountsByLabel"
                            @label-hover="onChartLabelHover"
                            @label-hover-leave="onChartLabelLeave"
                            @label-click="onChartLabelClick"
                          />
                          <BoxPlotComponent
                            v-else-if="isBoxplotChart(chartCfg)"
                            :chart-config="chartCfg"
                            :dimensions="dimensions"
                          />
                        </template>
                        <div
                          v-else
                          class="flex flex-col items-center justify-center text-center w-full
                            min-h-[220px] sm:min-h-[280px] px-4 text-gray-500"
                        >
                          <i class="pi pi-chart-bar text-3xl mb-2 opacity-40"></i>
                          <p class="text-sm sm:text-base max-w-md">
                            {{ getChartEmptyPlaceholderText() }}
                          </p>
                        </div>
                      </div>

                      <!-- Error bar hint -->
                      <p
                        v-if="shouldShowChartVisualization(tab, chartCfg)
                          && hasErrorBarsInConfig(chartCfg)"
                        class="text-sm sm:text-base text-gray-400 mt-2
                          text-center cursor-help"
                        v-tooltip.top="errorBarTooltipText"
                      >
                        <i class="pi pi-info-circle text-sm"></i>
                        {{ $t('ResultsInsightsPanel.errorBarHint') }}
                      </p>
                      <!-- Boxplot hint -->
                      <div
                        v-if="chartCfg?.type === 'boxplot'"
                        class="flex flex-col sm:flex-row gap-2
                          text-sm sm:text-base text-gray-400 mt-2"
                      >
                        <div>
                          <p class="text-gray-600">
                            {{ $t('MyResultsDashboardComponent.boxPlotDepictionDistribution') }}:</p>
                          <ul class="list-disc list-inside">
                            <li>{{ $t('MyResultsDashboardComponent.depiction1') }}</li>
                            <li>{{ $t('MyResultsDashboardComponent.depiction2') }}</li>
                            <li>{{ $t('MyResultsDashboardComponent.depiction3') }}</li>
                            <li>{{ $t('MyResultsDashboardComponent.depiction4') }}</li>
                            <li>{{ $t('MyResultsDashboardComponent.depiction5') }}</li>
                          </ul>
                        </div>
                        <div>
                          <p class="text-gray-600">
                            {{ $t('MyResultsDashboardComponent.boxPlotGraphInterpretation') }}:</p>
                          <ul class="list-disc list-inside">
                            <li>{{ $t('MyResultsDashboardComponent.interpretation1') }}</li>
                            <li>{{ $t('MyResultsDashboardComponent.interpretation2') }}</li>
                            <li>{{ $t('MyResultsDashboardComponent.interpretation3') }}</li>
                          </ul>
                        </div>
                      </div>

                      <BoxPlotStatsSection
                        v-if="shouldShowChartVisualization(tab, chartCfg)
                          && chartCfg?.type === 'boxplot'"
                        :rows="getBoxplotStatsRowsForTab(tab, chartCfg)"
                        :series-caption="getBoxplotStatsSeriesCaption(tab, chartCfg)"
                      />
                    </div>

                    <LikertScaleLegend
                      v-if="tabHasLikertScaleChart(tab)"
                      variant="sectionEnd"
                    />
                  </div>
                </div>

                <!-- Mobile: Compact Insight Block -->
                <div
                  v-if="isMobile && tab.generatedFromSchema
                   && stickyResultsData.length > 0"
                  class="bg-white rounded-lg shadow-sm p-4 space-y-3"
                >
                  <p class="text-sm font-semibold text-gray-700">
                    {{ $t('MyResultsDashboard.lastReflection')
                      || 'Latest Reflection' }}
                  </p>
                  <div
                    v-if="mobileInsight"
                    class="flex items-baseline gap-2"
                  >
                    <span class="text-2xl font-bold text-gray-800">
                      {{ formatOwnResultNumber(mobileInsight.lastKpi) }}
                    </span>
                    <span
                      v-if="mobileInsight.delta !== null"
                      class="text-sm font-medium"
                      :class="mobileInsight.delta >= 0
                        ? 'text-green-600' : 'text-red-600'"
                    >
                      {{ mobileInsight.delta >= 0 ? '+' : '' }}{{ formatOwnResultNumber(mobileInsight.delta) }}
                      {{ $t('MyResultsDashboard.fromLastWeek')
                        || 'from last week' }}
                    </span>
                  </div>
                  <p
                    v-if="mobileInsight && mobileInsight.firstAnswer"
                    class="text-xs text-gray-600 line-clamp-2"
                  >
                    {{ mobileInsight.firstAnswer }}
                  </p>
                </div>

                <!-- Details Card (line charts only) -->
                <div
                  v-if="!isMobile && tab.generatedFromSchema
                   && activeDetailEntry
                   && lineChartConfigForTab(tab)"
                  class="bg-white rounded-lg shadow-sm p-4 sm:p-5"
                >
                  <WeekDetailPanel
                    :selected-data="{
                      dateLabel: activeDetailEntry.completedAt
                        ? new Date(activeDetailEntry.completedAt)
                          .toISOString().split('T')[0]
                        : '',
                      completedAt: activeDetailEntry.completedAt,
                      kpiValues: buildDetailKpiValues(tab, activeDetailEntry),
                      openAnswers: activeDetailEntry.openAnswers || [],
                      stickyAnswers: activeDetailEntry.stickyAnswers || [],
                    }"
                    :dimensions="dimensions"
                    :dimension-colors="chartDimensionColors"
                  />
                </div>

                <!-- Results List Card (line charts only) -->
                <div
                  v-if="tab.generatedFromSchema
                   && lineChartConfigForTab(tab)"
                  class="bg-white rounded-lg shadow-sm p-4 sm:p-5"
                >
                  <StickyQuestionResults
                    :schema-id="tab.generatedFromSchema"
                    :selected-assessment-id="selectedAssessmentId"
                    :hovered-date="hoveredChartLabel"
                    :is-mobile="isMobile"
                    :dimensions="dimensions"
                    :dimension-colors="chartDimensionColors"
                    @select-entry="onTimelineSelectEntry"
                    @data-loaded="onStickyDataLoaded"
                  />
                </div>

                <!-- SECTION 3: Insights (scores, changes, context) -->
                <div>
                  <ResultsInsightsPanel
                    :overallAverage="getOverallAverage(tab)"
                    :dimensionEntries="getDimensionEntries(tab)"
                    :dimensionChanges="getDimensionChanges(tab)"
                    :changeSummary="getChangeSummary(tab)"
                    :previousDate="getPreviousDate(tab)"
                    :descriptions="currentPersonalizedDescriptions"
                    :statisticalContext="getStatisticalContext(tab)"
                    :currentScores="getCurrentScores(tab)"
                    :showBarChartInsights="!lineChartConfigForTab(tab)"
                  >
                    <!-- KSS: champion stats -->
                    <template
                      v-if="isCurrentTabKSS"
                      #descriptions
                    >
                      <div
                        class="bg-white rounded-lg p-4">
                        <div class="flex items-center gap-2 mb-3">
                          <i class="pi pi-star text-orange-500"></i>
                          <h4
                            class="text-base sm:text-lg font-semibold text-gray-800"
                          >
                            {{ getKssHistoryTitle() }}
                          </h4>
                        </div>
                        <div
                          v-if="kssHistoryLoading"
                          class="flex items-center justify-center py-4"
                        >
                          <ProgressSpinner
                            style="width: 20px; height: 20px"
                          />
                        </div>
                        <div
                          v-else-if="kssHistoryData"
                          class="space-y-2"
                        >
                          <div class="flex items-center gap-3">
                            <span
                              class="bg-orange-100 text-orange-800
                                px-2 py-1 rounded font-semibold text-sm sm:text-base"
                            >
                              {{ kssHistoryData.kssChoosen || 0 }}x
                            </span>
                            <span class="text-sm sm:text-base text-gray-700">
                              {{ $t(
                                'DashboardKssHistoryComponent.positiveFeedback'
                              ) }}
                            </span>
                          </div>
                          <div class="flex items-center gap-3">
                            <span
                              class="bg-orange-100 text-orange-800
                                px-2 py-1 rounded font-semibold text-sm sm:text-base"
                            >
                              {{ kssHistoryData.kssTrackerCreated || 0 }}x
                            </span>
                            <span class="text-sm sm:text-base text-gray-700">
                              {{ $t(
                                'DashboardKssHistoryComponent.kssCreated'
                              ) }}
                            </span>
                          </div>
                          <div class="flex items-center gap-3">
                            <span
                              class="bg-orange-100 text-orange-800
                                px-2 py-1 rounded font-semibold text-sm sm:text-base"
                            >
                              {{ kssHistoryData.kssTrackerSubmitted || 0 }}x
                            </span>
                            <span class="text-sm sm:text-base text-gray-700">
                              {{ $t(
                                'DashboardKssHistoryComponent.kssSubmtted'
                              ) }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </template>
                  </ResultsInsightsPanel>
                </div>

                <!-- SECTION 4: KSS Trend (if applicable) -->
                <div
                  v-if="isCurrentTabKSS"
                  class="bg-white rounded-lg shadow-sm p-4 sm:p-5"
                >
                  <KssTrendSection
                    :historyData="null"
                    :historyLoading="false"
                    :historyError="null"
                    :historyTitle="getKssHistoryTitle()"
                    :meetings="kssMeetings"
                    :trendPlotData="kssTrendPlotData"
                    :trendBoxplotConfig="kssTrendBoxplotConfig"
                    :showBoxplot="kssShowBoxplot"
                    :trendLoading="kssTrendLoading"
                    :trendError="kssTrendError"
                    :dimensions="dimensions"
                    :meetingTimePeriod="kssMeetingTimePeriod"
                    :meetingCustomStart="kssMeetingCustomStart"
                    :meetingCustomEnd="kssMeetingCustomEnd"
                    :selectedMeetingObj="selectedKssMeeting"
                    :rawOccurrences="kssRawOccurrences"
                    :occurrenceDateRange="kssOccurrenceDateRange"
                    :occurrenceCustomStart="kssOccurrenceCustomStart"
                    :occurrenceCustomEnd="kssOccurrenceCustomEnd"
                    @meeting-select="onKssMeetingSelect"
                    @update:showBoxplot="onKssToggleBoxplot"
                    @time-period-change="onKssMeetingTimePeriodChange"
                    @apply-custom-range="applyKssMeetingCustomRange"
                    @update:meetingCustomStart="(v) => kssMeetingCustomStart = v"
                    @update:meetingCustomEnd="(v) => kssMeetingCustomEnd = v"
                    @update:occurrenceCustomStart="(v) => { kssOccurrenceCustomStart = v; }"
                    @update:occurrenceCustomEnd="(v) => { kssOccurrenceCustomEnd = v; }"
                    @occurrence-date-change="onKssOccurrenceDateChange"
                    @reset-occurrence-filter="resetKssOccurrenceFilter"
                  />
                </div>

              </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import BarChartComponent from './BarChartComponent.vue';
import LineChartComponent from './LineChartComponent.vue';
import BoxPlotComponent from './BoxPlotComponent.vue';
import BoxPlotStatsSection from './BoxPlotStatsSection.vue';
import LikertScaleLegend from './LikertScaleLegend.vue';
import ResultsInsightsPanel from './ResultsInsightsPanel.vue';
import ResultsComparisonSection from './ResultsComparisonSection.vue';
import KssTrendSection from './KssTrendSection.vue';
import StickyQuestionResults from './StickyQuestionResults.vue';
import WeekDetailPanel from './WeekDetailPanel.vue';
import ProgressSpinner from 'primevue/progressspinner';
import Skeleton from 'primevue/skeleton';
import Select from 'primevue/select';
import Tooltip from 'primevue/tooltip';
// eslint-disable-next-line import/no-cycle
import NetworkContent from '@/helpers/networking/NetworkContent';
import {
  formatLocaleDate,
  resolveLocaleForDateFormat,
} from '@/helpers/formatLocaleDate';
import {
  formatOwnResultNumber as fmtOwnResultNumber,
  roundToTwoDecimals,
} from '@/helpers/formatOwnResultNumber';
import {
  dimensionScoresFromRangeSliderQuestions,
  rangeSliderMeanForDimension,
} from '@/helpers/assessmentDimensionScore';
import { withBoxplotExactWhiskers, clampLikertBoxplotStat } from '@/helpers/boxplotStats';
import {
  buildBoxplotStatsRows,
  getBoxplotPrimaryDatasetIndex,
} from '@/helpers/boxplotStatsTable';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';

/** Non-Likert dimension on KSS / collaboration tracker — must not affect averages */
const KSS_CHAMPION_DIMENSION = 'kssChampion';

/** Same order as WeekDetailPanel.vue DIMENSION_COLORS — detail UI fallback when no chart color */
const DETAIL_DIMENSION_FALLBACK_COLORS = [
  '#3b82f6', '#22c55e', '#a855f7', '#f97316', '#06b6d4',
  '#ec4899', '#eab308', '#6366f1',
];

export default {
  name: 'MyResultsDashboardComponent',
  props: {
    tab: {
      type: String,
      default: null,
    },
  },
  components: {
    BarChartComponent,
    LineChartComponent,
    BoxPlotComponent,
    BoxPlotStatsSection,
    LikertScaleLegend,
    ResultsInsightsPanel,
    ResultsComparisonSection,
    KssTrendSection,
    StickyQuestionResults,
    WeekDetailPanel,
    ProgressSpinner,
    Skeleton,
    Select,
  },
  directives: {
    tooltip: Tooltip,
  },
  data() {
    return {
      selectedTab: this.tab,
      loading: true,
      error: null,
      selectedTimePeriod: 'all',
      customDateRange: null, // Store custom date range for filtering
      customStartDate: '',
      customEndDate: '',
      selectedTimeGranularity: 'weekly', // Daily, Weekly, Monthly
      selectedComparison1: {},
      selectedComparison2: {},
      groupedAssessments: {},
      originalChartConfigs: {},
      // TODO: REFACTOR - Remove hardcoded URL and integrate with new backend
      // WARNING: This is a temporary hardcoded solution for KSS champion data
      // The old backend API endpoint should be migrated to the new backend architecture
      kssHistoryData: null,
      kssHistoryLoading: false,
      kssHistoryError: null,
      // KSS Trend Analysis (KSS Evaluation Per Meeting)
      kssMeetings: [], // List of KSS meetings for dropdown
      selectedKssMeeting: null, // Currently selected meeting
      kssTrendPlotData: null, // Chart data for selected meeting (active view)
      kssTrendBarConfig: null, // Bar chart config (always generated)
      kssTrendBoxplotConfig: null, // Boxplot config (generated when n>=2)
      kssShowBoxplot: false, // Toggle: false=bars (default), true=boxplot
      kssTrendLoading: false,
      kssTrendError: null,
      // KSS Time Period Filters
      kssMeetingTimePeriod: 'all',
      kssMeetingCustomStart: '',
      kssMeetingCustomEnd: '',
      kssOccurrenceCustomStart: '',
      kssOccurrenceCustomEnd: '',
      kssRawOccurrences: null, // raw backend occurrences for re-filtering
      kssRawAssessments: null, // raw backend assessments for non-recurring
      hoveredChartLabel: null,
      selectedTimeBucket: null,
      selectedAssessmentId: null,
      chartSelectedAssessmentId: null,
      windowWidth: window.innerWidth,
      stickyResultsData: [],
    };
  },
  computed: {
    ...mapState('assessments', ['assessments', 'minOfDatasetsForAggResults']),
    ...mapState('assessmentSchemas', ['assessmentSchemas']),
    ...mapState('charts', ['charts']),
    ...mapState('dimensions', ['dimensions']),
    // Map getters from assessmentSchemas store for external evaluation support
    ...mapGetters('assessmentSchemas', [
      'isExternalEvaluationType',
      'getExternalEvaluationSchemaForType',
      'getParentTypeForExternalEvaluation',
      'getExternalEvaluationTypeForParent',
      'canCreateExternalEvaluationForType',
    ]),
    featureName() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'kssTracker',
        this.$t('FallbackTexts.collaborationTracker'),
        this.$i18n?.locale,
      );
    },
    isMobile() {
      return this.windowWidth < 768;
    },
    mobileInsight() {
      if (!this.stickyResultsData || this.stickyResultsData.length === 0) return null;
      const latest = this.stickyResultsData[0];
      const prev = this.stickyResultsData.length > 1 ? this.stickyResultsData[1] : null;

      const currentTab = this.latestAssessments?.find(
        (t) => t.assessmentId === this.selectedTab,
      );
      const rawLatest = latest.kpiValues || {};
      const kpiValues = currentTab
        ? this.buildDetailKpiValues(currentTab, latest)
        : rawLatest;
      const kpiKeys = Object.keys(kpiValues);
      if (kpiKeys.length === 0) {
        return {
          lastKpi: '--',
          delta: null,
          firstAnswer: latest.stickyAnswers?.[0]?.answer
            ? String(latest.stickyAnswers[0].answer).substring(0, 120)
            : null,
        };
      }

      const avg = kpiKeys.reduce((s, k) => s + Number(kpiValues[k] || 0), 0) / kpiKeys.length;
      const lastKpi = roundToTwoDecimals(avg);

      let delta = null;
      if (prev?.kpiValues && currentTab) {
        const prevFiltered = this.buildDetailKpiValues(currentTab, prev);
        const prevKeys = Object.keys(prevFiltered);
        if (prevKeys.length > 0) {
          const prevAvg = prevKeys.reduce(
            (s, k) => s + Number(prevFiltered[k] || 0), 0,
          ) / prevKeys.length;
          delta = roundToTwoDecimals(avg - prevAvg);
        }
      }

      const firstTextAnswer = latest.stickyAnswers?.find(
        (a) => a.type !== 'rangeSlider' && a.answer,
      );

      return {
        lastKpi,
        delta,
        firstAnswer: firstTextAnswer
          ? String(firstTextAnswer.answer).substring(0, 120)
          : null,
      };
    },
    chartDimensionColors() {
      const currentTab = this.latestAssessments?.find(
        (tab) => tab.assessmentId === this.selectedTab,
      );
      if (!currentTab) return {};

      const normalizeChartColor = (c) => {
        if (c == null || c === '') return null;
        if (Array.isArray(c)) return normalizeChartColor(c[0]);
        const s = String(c).trim();
        if (s.startsWith('#') || s.startsWith('rgb') || s.startsWith('hsl')) return s;
        return `#${s}`;
      };

      const pickFromDataset = (ds, barIndex) => {
        if (!ds) return null;
        const { borderColor: bc, backgroundColor: bg } = ds;
        if (Array.isArray(bg)) return normalizeChartColor(bg[barIndex] ?? bg[0]);
        if (bg) return normalizeChartColor(bg);
        if (Array.isArray(bc)) return normalizeChartColor(bc[barIndex] ?? bc[0]);
        if (bc) return normalizeChartColor(bc);
        return null;
      };

      const colors = {};
      const rawConfigs = currentTab.chartConfigs?.length
        ? currentTab.chartConfigs
        : (currentTab.chartConfig ? [currentTab.chartConfig] : []);
      const configs = [
        ...rawConfigs.filter((cfg) => cfg?.type === 'line'),
        ...rawConfigs.filter((cfg) => cfg?.type !== 'line'),
      ];

      const isAvgBarTitle = (cfg, label) => {
        if (!cfg?.averageData?.enabled || label == null) return false;
        const locale = this.$i18n?.locale || 'en';
        const avgTitle = locale === 'de'
          ? cfg.averageData.title?.de
          : cfg.averageData.title?.en;
        return label === avgTitle || label === 'Average';
      };

      configs.forEach((cfg) => {
        if (!cfg?.type) return;
        const datasets = cfg.data?.datasets || [];
        const { type } = cfg;

        if (type === 'line') {
          const locale = this.$i18n?.locale || 'en';
          const avgTitle = cfg.averageData?.enabled
            ? (locale === 'de' ? cfg.averageData.title?.de : cfg.averageData.title?.en)
            : null;
          datasets.forEach((ds, dsIdx) => {
            const lbl = ds?.label;
            if (lbl == null || lbl === '') return;
            if (avgTitle && (lbl === avgTitle || lbl === 'Average')) return;
            const key = this.resolveDimensionKeyForLineDatasetLabel(currentTab, lbl, cfg, dsIdx);
            if (key == null) return;
            const color = pickFromDataset(ds, 0);
            if (color && !colors[key]) colors[key] = color;
          });
          return;
        }

        if (type === 'bar' || type === 'barWithErrorBars' || type === 'boxplot') {
          const labels = cfg.data?.labels || [];
          const ds0 = datasets[0];
          labels.forEach((label, idx) => {
            if (isAvgBarTitle(cfg, label)) return;
            const resolved = this.resolveChartCategoryToDimensionKey(
              currentTab,
              label,
              cfg,
            );
            const dimKey = resolved || label;
            const color = pickFromDataset(ds0, idx);
            if (color && dimKey && !colors[dimKey]) colors[dimKey] = color;
          });
        }
      });

      const detailKeys = this.getAssessmentDetailDimensionKeys(currentTab);
      detailKeys.forEach((key, idx) => {
        if (!colors[key]) {
          colors[key] = DETAIL_DIMENSION_FALLBACK_COLORS[
            idx % DETAIL_DIMENSION_FALLBACK_COLORS.length
          ];
        }
        colors[`__idx_${idx}`] = colors[key];
      });

      return colors;
    },
    activeDetailEntry() {
      if (this.chartSelectedAssessmentId && this.stickyResultsData.length > 0) {
        const match = this.stickyResultsData.find(
          (e) => e.assessmentId === this.chartSelectedAssessmentId,
        );
        if (match) return match;
      }
      return this.stickyResultsData[0] || null;
    },
    stickyAnswerCountsByLabel() {
      if (!this.stickyResultsData || this.stickyResultsData.length === 0) return {};
      const counts = {};
      this.stickyResultsData.forEach((entry) => {
        if (!entry.completedAt) return;
        const date = new Date(entry.completedAt);
        const labelKey = this.dateToChartLabelKey(date);
        const textFieldCount = (entry.openAnswers || [])
          .filter((a) => a.answer != null && a.answer !== '').length;
        const stickyTextCount = (entry.stickyAnswers || [])
          .filter((a) => a.type === 'textField' && a.answer != null && a.answer !== '').length;
        counts[labelKey] = (counts[labelKey] || 0)
          + Math.max(textFieldCount, stickyTextCount);
      });
      return counts;
    },
    selectedTimeBucketData() {
      if (!this.stickyResultsData || this.stickyResultsData.length === 0) {
        return null;
      }
      let matching = null;
      if (this.selectedAssessmentId) {
        matching = this.stickyResultsData.find(
          (e) => e.assessmentId === this.selectedAssessmentId,
        );
      } else if (this.selectedTimeBucket) {
        matching = this.stickyResultsData.find(
          (e) => this.entryMatchesDateLabel(e, this.selectedTimeBucket),
        );
      }
      if (!matching) return null;
      return {
        dateLabel: this.selectedTimeBucket
          || new Date(matching.completedAt).toISOString().split('T')[0],
        kpiValues: matching.kpiValues || {},
        stickyAnswers: matching.stickyAnswers || [],
        completedAt: matching.completedAt,
      };
    },
    latestAssessments() {
      if (!this.assessments) return [];

      // Group assessments by type
      const groupedAssessments = this.assessments.reduce((acc, assessment) => {
        if (!acc[assessment.assessmentType]) {
          acc[assessment.assessmentType] = [];
        }
        acc[assessment.assessmentType].push(assessment);
        return acc;
      }, {});

      // Get latest assessment for each type
      const latestAssessments = Object.entries(groupedAssessments)
        .map(([, assessments]) => {
          const latest = assessments.sort(
            (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt),
          )[0];
          return latest;
        });

      return latestAssessments;
    },

    /**
     * Get all external evaluation assessments grouped by their type
     * Uses the isExternalEvaluationSchema flag from schemas to identify external evaluations
     */
    externalEvaluationAssessments() {
      if (!this.groupedAssessments || !this.assessmentSchemas) return {};

      const externalEvalTypes = this.assessmentSchemas
        .filter((schema) => schema.isExternalEvaluationSchema === true)
        .map((schema) => schema.type);

      const result = {};
      externalEvalTypes.forEach((type) => {
        if (this.groupedAssessments[type] && this.groupedAssessments[type].length > 0) {
          result[type] = this.groupedAssessments[type];
        }
      });
      return result;
    },

    /**
     * Check if there are any external evaluation assessments available
     */
    hasExternalEvaluations() {
      return Object.keys(this.externalEvaluationAssessments).length > 0;
    },

    /**
     * Check if the currently selected tab is a KSS assessment
     */
    isCurrentTabKSS() {
      if (!this.selectedTab || !this.latestAssessments) return false;
      const currentTab = this.latestAssessments
        .find((tab) => tab.assessmentId === this.selectedTab);
      return currentTab && currentTab.assessmentType === 'kss';
    },

    kssOccurrenceDateRange() {
      if (!this.kssRawOccurrences || this.kssRawOccurrences.length === 0) {
        return { min: '', max: '' };
      }
      const dates = this.kssRawOccurrences.map((o) => new Date(o.date));
      const min = new Date(Math.min(...dates));
      const max = new Date(Math.max(...dates));
      return {
        min: min.toISOString().slice(0, 10),
        max: max.toISOString().slice(0, 10),
      };
    },

    filteredKssMeetings() {
      return this.filterByTimePeriod(
        this.kssMeetings,
        this.kssMeetingTimePeriod,
        this.kssMeetingCustomStart,
        this.kssMeetingCustomEnd,
        'date_taskBegin',
        'date_assessmentMade',
      );
    },

    /**
     * Get personalized result descriptions for the current selected tab
     */
    currentPersonalizedDescriptions() {
      if (!this.selectedTab || !this.latestAssessments) return [];
      const currentTab = this.latestAssessments.find(
        (tab) => tab.assessmentId === this.selectedTab,
      );
      if (!currentTab?.results?.dimensions || !this.dimensions) {
        return [];
      }

      const locale = this.$i18n?.locale || 'en';
      const scores = this.getCurrentScores(currentTab);

      const dimensionResults = Object.entries(scores)
        .filter(([, value]) => typeof value === 'number' && !Number.isNaN(value))
        .map(([dimensionValue, value]) => ({
          dimensionValue,
          value,
          dimension: this.dimensions.find((d) => d.value === dimensionValue),
        }))
        .filter((item) => item.dimension);

      // Sort by value (highest to lowest)
      dimensionResults.sort((a, b) => b.value - a.value);

      const descriptions = [];

      // Top 2 dimensions (highest results) - show positive descriptions
      dimensionResults.slice(0, 2).forEach((item, index) => {
        const positiveDesc = item.dimension.resultDescriptions?.find(
          (desc) => desc.status === 'positive',
        );
        if (positiveDesc) {
          // Try to get translation from dimension object first
          const translation = item.dimension.translation?.[locale]
           || item.dimension.translation?.en;
          // If translation exists and is not empty,
          //  use it; otherwise try getDimensionLabel; finally fallback to dimensionValue
          const dimensionName = (translation && translation.trim())
            ? translation
            : (this.getDimensionLabel(item.dimensionValue) || item.dimensionValue || '');

          // Get headline from result description
          let headline = positiveDesc.headline?.[locale] || positiveDesc.headline?.en || '';

          // If headline is empty, use dimension name as headline
          if (!headline || !headline.trim()) {
            headline = dimensionName;
          }

          descriptions.push({
            type: 'positive',
            rank: index + 1,
            dimensionName,
            headline,
            content: positiveDesc.content?.[locale] || positiveDesc.content?.en || '',
            tip: positiveDesc.tip?.[locale] || positiveDesc.tip?.en || '',
          });
        }
      });

      // Bottom 2 dimensions (lowest results) - show negative descriptions with tips
      dimensionResults
        .slice(-2)
        .reverse()
        .forEach((item, index) => {
          const negativeDesc = item.dimension.resultDescriptions?.find(
            (desc) => desc.status === 'negative',
          );
          if (negativeDesc) {
            // Try to get translation from dimension object first
            const translation = item.dimension.translation?.[locale]
             || item.dimension.translation?.en;
            // If translation exists and is not empty,
            //  use it; otherwise try getDimensionLabel; finally fallback to dimensionValue
            const dimensionName = (translation && translation.trim())
              ? translation
              : (this.getDimensionLabel(item.dimensionValue) || item.dimensionValue || '');

            // Get headline from result description
            let headline = negativeDesc.headline?.[locale] || negativeDesc.headline?.en || '';

            // If headline is empty, use dimension name as headline
            if (!headline || !headline.trim()) {
              headline = dimensionName;
            }

            descriptions.push({
              type: 'negative',
              rank: dimensionResults.length - 1 - index, // Reverse rank for bottom dimensions
              dimensionName,
              headline,
              content: negativeDesc.content?.[locale] || negativeDesc.content?.en || '',
              tip: negativeDesc.tip?.[locale] || negativeDesc.tip?.en || '',
            });
          }
        });

      return descriptions;
    },
    /**
     * Get the number of dimensions for the current tab
     */
    currentDimensionCount() {
      if (!this.selectedTab || !this.latestAssessments) return 0;
      const currentTab = this.latestAssessments.find(
        (tab) => tab.assessmentId === this.selectedTab,
      );
      if (!currentTab) return 0;

      const keys = this.getAssessmentDetailDimensionKeys(currentTab);
      if (keys.length > 0) return keys.length;

      if (currentTab?.dimensions && Array.isArray(currentTab.dimensions)) {
        return currentTab.dimensions.length;
      }
      if (currentTab?.results?.dimensions) {
        const rk = Object.keys(
          this.dimensionsWithoutKssChampion(currentTab.results.dimensions, currentTab),
        );
        return rk.length;
      }
      return 0;
    },

    /**
     * Options for the time period PrimeVue Select
     */
    timePeriodOptions() {
      return [
        { value: 'all', label: this.$t('TimePeriodSelector.allTime') || 'All Time' },
        { value: 'week', label: this.$t('TimePeriodSelector.lastWeek') || 'Last Week' },
        { value: 'month', label: this.$t('TimePeriodSelector.lastMonth') || 'Last Month' },
        { value: 'quarter', label: this.$t('TimePeriodSelector.lastQuarter') || 'Last Quarter' },
        { value: 'year', label: this.$t('TimePeriodSelector.lastYear') || 'Last Year' },
        { value: 'custom', label: this.$t('TimePeriodSelector.customRange') || 'Custom Range' },
      ];
    },

    /**
     * Options for the time granularity PrimeVue Select
     */
    granularityOptions() {
      return [
        { value: 'daily', label: this.$t('MyResultsDashboardComponent.daily') || 'Daily' },
        { value: 'weekly', label: this.$t('MyResultsDashboardComponent.weekly') || 'Weekly' },
        { value: 'monthly', label: this.$t('MyResultsDashboardComponent.monthly') || 'Monthly' },
      ];
    },

    errorBarTooltipText() {
      return this.$t('ResultsInsightsPanel.errorBarExplanation');
    },
  },
  methods: {
    ...mapActions('assessments', ['getUserAssessmentsResultsForDashboard', 'getUserExternalEvaluationResults']),
    ...mapActions('assessmentSchemas', ['fetchAssessments']),
    ...mapActions('charts', ['fetchCharts']),
    ...mapActions('dimensions', ['fetchDimensions']),

    formatResultsDate(value) {
      return formatLocaleDate(value, this.$i18n?.locale);
    },

    formatOwnResultNumber(value) {
      return fmtOwnResultNumber(value);
    },

    getChartDescription(chartCfg) {
      if (!chartCfg?.description) return '';
      const locale = this.$i18n?.locale || 'en';
      const txt = chartCfg.description?.[locale] || chartCfg.description?.en || chartCfg.description?.de;
      return typeof txt === 'string' ? txt.trim() : '';
    },

    isLikertScaleChartConfig(chartCfg) {
      const y = chartCfg?.options?.scales?.y;
      if (!y || y.min !== 0) return false;
      const max = y.max ?? y.suggestedMax;
      return max === 5 || max === 6;
    },

    tabHasLikertScaleChart(tab) {
      const configs = tab?.chartConfigs?.length
        ? tab.chartConfigs
        : (tab?.chartConfig ? [tab.chartConfig] : []);
      return configs.some((cfg) => this.isLikertScaleChartConfig(cfg));
    },

    calculateStandardDeviation(values) {
      const n = values.length;
      if (n === 0) return 0;

      const mean = values.reduce((sum, val) => sum + val, 0) / n;
      const squaredDiffs = values.map((val) => (val - mean) ** 2);
      const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / n;
      return Math.sqrt(variance);
    },

    /**
     * Bar chart saved as type bar with barBoxplotWhenAggregated: use boxplot when
     * own-results scope is enabled and assessment count meets tenant minOfDatasetsForAggResults.
     */
    lineChartConfigForTab(tab) {
      if (!tab) return null;
      if (tab.chartConfigs?.length) {
        const line = tab.chartConfigs.find((c) => this.isLineChart(c));
        if (line) return line;
      }
      return this.isLineChart(tab.chartConfig) ? tab.chartConfig : null;
    },

    shouldRenderBarChartAsBoxplotForOwnResults(tab, chartIndex = 0) {
      const rawOrig = this.originalChartConfigs[tab.assessmentId];
      const orig = Array.isArray(rawOrig) ? rawOrig[chartIndex] : rawOrig;
      const cfg = orig || tab.chartConfig;
      const vis = cfg?.visibility ?? 'both';
      if (!['own', 'both'].includes(vis)) return false;
      const bb = cfg?.barBoxplotWhenAggregated;
      if (!bb?.enabled || !bb.ownResults) return false;
      const min = this.minOfDatasetsForAggResults ?? 5;
      const n = this.groupedAssessments[tab.assessmentType]?.length ?? 0;
      return n >= min;
    },

    /**
     * Compute quartiles for boxplot. Returns { min, q1, median, q3, max }.
     * @param {number[]} values - Sorted or unsorted array of numbers
     * @returns {{ min: number, q1: number, median: number, q3: number, max: number }|null}
     */
    computeQuartiles(values) {
      if (!values || values.length === 0) return null;
      const sorted = [...values].filter((v) => v != null && !Number.isNaN(v)).sort((a, b) => a - b);
      const n = sorted.length;
      if (n === 0) return null;
      const min = sorted[0];
      const max = sorted[n - 1];
      const q1Idx = (n - 1) * 0.25;
      const medIdx = (n - 1) * 0.5;
      const q3Idx = (n - 1) * 0.75;
      const lerp = (arr, i) => {
        const lo = Math.floor(i);
        const hi = Math.min(lo + 1, n - 1);
        const t = i - lo;
        return arr[lo] * (1 - t) + arr[hi] * t;
      };
      return {
        min,
        q1: lerp(sorted, q1Idx),
        median: lerp(sorted, medIdx),
        q3: lerp(sorted, q3Idx),
        max,
      };
    },

    /**
     * Dimension keys for an assessment (boxplot / comparison helpers).
     * @param {Object} assessment
     * @returns {string[]}
     */
    getAssessmentDimensionKeysForBoxplot(assessment) {
      if (this.usesStandardDimensionsStructure(assessment)) {
        return Object.keys(assessment.results.dimensions || {});
      }
      if (this.usesQuestionArrayStructure(assessment)) {
        return (assessment.assessmentQuestionArray || [])
          .map((q) => q.properties?.dimensionName)
          .filter(Boolean);
      }
      return [];
    },

    /**
     * Read a per-dimension numeric from results maps (key, label, or translated label match).
     */
    pickResultsDimensionNumber(map, key, label) {
      if (!map || typeof map !== 'object') return undefined;
      if (typeof map[key] === 'number' && !Number.isNaN(map[key])) return map[key];
      if (typeof map[label] === 'number' && !Number.isNaN(map[label])) return map[label];
      const alt = Object.keys(map).find((k) => this.getDimensionLabel(k) === label);
      if (alt != null && typeof map[alt] === 'number' && !Number.isNaN(map[alt])) return map[alt];
      return undefined;
    },

    /**
     * Boxplot stats: prefer backend empirical quartiles (aggregated external evals), else mean ± SD.
     */
    getBoxplotMeanSdApproximation(assessment, key, label) {
      let mean;
      if (this.usesStandardDimensionsStructure(assessment)) {
        mean = assessment.results?.dimensions?.[key];
      } else if (this.usesQuestionArrayStructure(assessment)) {
        mean = rangeSliderMeanForDimension(assessment.assessmentQuestionArray, key);
      } else {
        mean = assessment.results?.[key];
      }

      if (mean == null || Number.isNaN(Number(mean))) return null;
      mean = Number(mean);

      let sd = 0;
      const dimensionData = assessment?.originalTaskData?.taskEvents?.[0]?.data?.dimensionData;
      if (dimensionData?.length) {
        const dim = dimensionData.find(
          (d) => d.dimensionName === label || d.dimensionValue === label
            || d.name === label || this.getDimensionLabel(d.dimensionValue) === label,
        );
        if (dim != null && typeof dim.std === 'number') sd = dim.std;
      } else if (assessment?.results?.dimensions_stdDev) {
        const stdDev = this.pickResultsDimensionNumber(
          assessment.results.dimensions_stdDev,
          key,
          label,
        );
        if (typeof stdDev === 'number') sd = stdDev;
      }

      const res = assessment?.results;
      const med = res?.dimensions_median
        ? this.pickResultsDimensionNumber(res.dimensions_median, key, label)
        : undefined;
      const q1v = res?.dimensions_q1
        ? this.pickResultsDimensionNumber(res.dimensions_q1, key, label)
        : undefined;
      const q3v = res?.dimensions_q3
        ? this.pickResultsDimensionNumber(res.dimensions_q3, key, label)
        : undefined;
      const smin = res?.dimensions_sampleMin
        ? this.pickResultsDimensionNumber(res.dimensions_sampleMin, key, label)
        : undefined;
      const smax = res?.dimensions_sampleMax
        ? this.pickResultsDimensionNumber(res.dimensions_sampleMax, key, label)
        : undefined;

      if (
        med != null
        && q1v != null
        && q3v != null
        && smin != null
        && smax != null
      ) {
        const clamp = (v) => clampLikertBoxplotStat(v);
        return withBoxplotExactWhiskers({
          min: clamp(smin),
          q1: clamp(q1v),
          median: clamp(med),
          q3: clamp(q3v),
          max: clamp(smax),
          mean,
          sd,
        });
      }

      const lo = clampLikertBoxplotStat(mean - (sd || 0));
      const hi = Math.min(5, mean + (sd || 0));
      const q1 = clampLikertBoxplotStat(mean - 0.67 * (sd || 0));
      const q3 = Math.min(5, mean + 0.67 * (sd || 0));
      return withBoxplotExactWhiskers({
        min: lo,
        q1,
        median: mean,
        q3,
        max: hi,
        mean,
        sd,
      });
    },

    /**
     * One boxplot item for a single assessment at a chart label (dimension display or key).
     * @param {Object} assessment
     * @param {string} label - Chart label (dimension value)
     * @returns {Object|null} Box stats for chartjs-chart-boxplot
     */
    getBoxplotPointForAssessmentDimension(assessment, label) {
      const keys = this.getAssessmentDimensionKeysForBoxplot(assessment);
      const key = keys.find((k) => k === label || this.getDimensionLabel(k) === label);
      if (!key) return null;

      return this.getBoxplotMeanSdApproximation(assessment, key, label);
    },

    /**
     * Per-dimension boxplot row for one assessment (parallel to dimensionLabels).
     */
    buildBoxplotDataRowForAssessment(assessment, dimensionLabels) {
      return dimensionLabels.map((label) => this.getBoxplotPointForAssessmentDimension(
        assessment,
        label,
      ));
    },

    /**
     * Normalize chart config border/fill color for boxplot dataset borderColor.
     * @param {string|string[]|undefined|null} color
     * @returns {string|null}
     */
    normalizeHexColorForBoxplot(color) {
      if (color == null) return null;
      const raw = typeof color === 'string' ? color : (Array.isArray(color) ? color[0] : null);
      if (raw == null || typeof raw !== 'string' || !String(raw).trim()) return null;
      const s = String(raw).trim();
      if (s.startsWith('#') || s.startsWith('rgb')) return s;
      return `#${s}`;
    },

    /**
     * Build chart config for boxplot from current/comparison assessments.
     * Mirrors bar-chart comparison: multiple datasets, grey vs accent, same label pattern.
     * @param {Object} tab - Processed tab with chartConfig and assessmentType
     * @returns {Object|null} Chart config with type 'boxplot' or null
     */
    buildBoxplotConfigForExternalEval(tab, chartIndex = 0) {
      const assessmentId = tab.assessmentId;
      const hasComparison1 = !!this.selectedComparison1[assessmentId];
      const hasComparison2 = !!this.selectedComparison2[assessmentId];

      const rawOrig = this.originalChartConfigs[assessmentId];
      const orig = Array.isArray(rawOrig) ? rawOrig[chartIndex] : rawOrig;
      const sourceConfig = orig
        ? JSON.parse(JSON.stringify(orig))
        : JSON.parse(JSON.stringify(tab.chartConfig));

      const chartLabels = sourceConfig?.data?.labels;
      if (!chartLabels || chartLabels.length === 0) return null;

      const hasAverageBar = sourceConfig.averageData?.enabled
        && sourceConfig.averageData?.dimensions?.length;
      const dimensionLabels = hasAverageBar
        ? chartLabels.slice(0, -1)
        : chartLabels;

      const latest = this.groupedAssessments[tab.assessmentType]?.[0];
      if (!latest) return null;

      const ds0 = sourceConfig?.data?.datasets?.[0];
      const defaultBg = ds0?.backgroundColor ?? 'rgba(200, 58, 121, 0.7)';
      const defaultBc = this.normalizeHexColorForBoxplot(ds0?.borderColor) ?? '#000000';
      const borderWidth = ds0?.borderWidth ?? 1;

      const barChartConfigColor = (() => {
        const bg = ds0?.backgroundColor;
        const bc = ds0?.borderColor;
        const color = (typeof bg === 'string' ? bg : bg?.[0])
          || (typeof bc === 'string' ? bc : bc?.[0]);
        const normalized = color && (color.startsWith('#') ? color : `#${color}`);
        return normalized || '#f97316';
      })();
      /** Stroke for accent / latest series: keep chart config border when set */
      const accentBorderColor = this.normalizeHexColorForBoxplot(ds0?.borderColor)
        ?? '#000000';
      const barChartGreyColor = '#9ca3af';
      /** Grey comparison boxes: grey fill, black outline (visible on light background) */
      const barChartGreyBorderColor = '#000000';

      const getComparisonDateLabel = (a) => ((a?.date_assessmentMade || a?.submittedAt)
        ? formatLocaleDate(a.date_assessmentMade || a.submittedAt, this.$i18n?.locale)
        : '');
      const getComparisonTypeLabel = (comparisonId) => {
        const ctype = this.getComparisonType(comparisonId);
        return ctype === 'external'
          ? `(${this.$t('MyResultsDashboardComponent.external') || 'External'})`
          : '';
      };

      const locale = this.$i18n?.locale || 'en';
      const avgData = sourceConfig.averageData;
      const averageLabelText = hasAverageBar
        ? (locale === 'de' ? avgData.title?.de : avgData.title?.en) || 'Average'
        : null;
      const labels = (hasAverageBar && averageLabelText)
        ? [...dimensionLabels, averageLabelText]
        : dimensionLabels;

      const datasets = [];

      const pushBoxDataset = (assessment, labelText, bg, bc) => {
        const row = this.buildBoxplotDataRowForAssessment(assessment, dimensionLabels);
        datasets.push({
          label: labelText,
          data: hasAverageBar ? [...row, null] : row,
          backgroundColor: bg,
          borderColor: bc,
          borderWidth,
        });
      };

      const assessmentTitle = this.getAssessmentName(tab.assessmentName);

      if (!hasComparison1 && !hasComparison2) {
        pushBoxDataset(latest, assessmentTitle, defaultBg, defaultBc);
      }

      if (hasComparison1 && !hasComparison2) {
        const leftAssessment = this.findComparisonAssessment(
          this.selectedComparison1[assessmentId],
          tab.assessmentType,
        );
        if (leftAssessment) {
          pushBoxDataset(
            leftAssessment,
            `${assessmentTitle} ${getComparisonDateLabel(leftAssessment)} ${getComparisonTypeLabel(this.selectedComparison1[assessmentId])}`,
            barChartGreyColor,
            barChartGreyBorderColor,
          );
        }
        pushBoxDataset(latest, assessmentTitle, barChartConfigColor, accentBorderColor);
      }

      if (!hasComparison1 && hasComparison2) {
        pushBoxDataset(latest, assessmentTitle, barChartGreyColor, barChartGreyBorderColor);
        const rightAssessment = this.findComparisonAssessment(
          this.selectedComparison2[assessmentId],
          tab.assessmentType,
        );
        if (rightAssessment) {
          pushBoxDataset(
            rightAssessment,
            `${assessmentTitle} ${getComparisonDateLabel(rightAssessment)} ${getComparisonTypeLabel(this.selectedComparison2[assessmentId])}`,
            barChartConfigColor,
            accentBorderColor,
          );
        }
      }

      if (hasComparison1 && hasComparison2) {
        const leftAssessment = this.findComparisonAssessment(
          this.selectedComparison1[assessmentId],
          tab.assessmentType,
        );
        const rightAssessment = this.findComparisonAssessment(
          this.selectedComparison2[assessmentId],
          tab.assessmentType,
        );
        if (leftAssessment) {
          pushBoxDataset(
            leftAssessment,
            `${assessmentTitle} ${getComparisonDateLabel(leftAssessment)} ${getComparisonTypeLabel(this.selectedComparison1[assessmentId])}`,
            barChartGreyColor,
            barChartGreyBorderColor,
          );
        }
        if (rightAssessment) {
          pushBoxDataset(
            rightAssessment,
            `${assessmentTitle} ${getComparisonDateLabel(rightAssessment)} ${getComparisonTypeLabel(this.selectedComparison2[assessmentId])}`,
            barChartConfigColor,
            accentBorderColor,
          );
        }
      }

      if (hasAverageBar && averageLabelText) {
        const tempConfig = {
          ...sourceConfig,
          data: { ...sourceConfig.data, labels },
        };
        const barAvg = this.createAverageDatasetForBarChart(latest, tempConfig);
        if (barAvg) {
          datasets.push({
            label: barAvg.label,
            data: barAvg.data.map((v) => {
              if (v == null) return null;
              if (typeof v === 'number') {
                return {
                  min: v,
                  q1: v,
                  median: v,
                  q3: v,
                  max: v,
                  mean: v,
                  sd: 0,
                };
              }
              return v;
            }),
            backgroundColor: barAvg.backgroundColor,
            borderColor: this.normalizeHexColorForBoxplot(barAvg.borderColor)
              ?? accentBorderColor,
            borderWidth,
          });
        }
      }

      if (datasets.length === 0) return null;

      return {
        type: 'boxplot',
        data: {
          labels,
          datasets,
        },
        options: {
          plugins: {
            legend: { display: datasets.length > 1 },
          },
          scales: {
            y: { min: 0, max: 5 },
          },
        },
      };
    },

    // Smart time granularity selection based on data range
    selectSmartTimeGranularity(assessments) {
      if (assessments.length === 0) return 'weekly';

      const dates = assessments
        .map((a) => new Date(a.date_assessmentMade || a.submittedAt || a.date || a.createdAt))
        .sort((a, b) => a - b);

      const firstDate = dates[0];
      const lastDate = dates[dates.length - 1];
      const daysDiff = Math.ceil((lastDate - firstDate) / (1000 * 60 * 60 * 24));

      // Smart defaults based on data range
      if (daysDiff < 7) {
        return 'daily'; // Less than a week: show daily
      }
      if (daysDiff < 30) {
        return 'weekly'; // Less than a month: show weekly
      }
      return 'monthly'; // More than a month: show monthly
    },

    // Group assessments by time period based on selected granularity
    groupAssessmentsByTimePeriod(assessments, granularity) {
      if (granularity === 'daily') {
        // For daily, return individual assessments grouped by date
        const grouped = {};
        assessments.forEach((assessment) => {
          const date = new Date(assessment.date_assessmentMade
           || assessment.submittedAt
           || assessment.date
           || assessment.createdAt);
          const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
          if (!grouped[dateKey]) {
            grouped[dateKey] = [];
          }
          grouped[dateKey].push(assessment);
        });
        return grouped;
      }
      if (granularity === 'weekly') {
        // Group by week (Monday to Sunday)
        const grouped = {};
        assessments.forEach((assessment) => {
          const date = new Date(assessment.date_assessmentMade
           || assessment.submittedAt
           || assessment.date
           || assessment.createdAt);
          const weekStart = new Date(date);
          // Get Monday of the week (handle Sunday as day 0)
          const dayOfWeek = date.getDay();
          const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
          weekStart.setDate(date.getDate() + daysToMonday);
          weekStart.setHours(0, 0, 0, 0); // Set to start of day for consistency
          const weekKey = weekStart.toISOString().split('T')[0]; // YYYY-MM-DD format
          if (!grouped[weekKey]) {
            grouped[weekKey] = [];
          }
          grouped[weekKey].push(assessment);
        });
        return grouped;
      }
      if (granularity === 'monthly') {
        // Group by month
        const grouped = {};
        assessments.forEach((assessment) => {
          const date = new Date(assessment.date_assessmentMade
           || assessment.submittedAt
           || assessment.date
           || assessment.createdAt);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`; // YYYY-MM-01 format
          if (!grouped[monthKey]) {
            grouped[monthKey] = [];
          }
          grouped[monthKey].push(assessment);
        });
        return grouped;
      }
      return {};
    },

    // Map chart dimension names to assessment data dimension names
    mapDimensionName(chartDimensionName, assessment) {
      // If assessment has assessmentQuestionArray, try to find matching dimension
      if (assessment?.assessmentQuestionArray?.length > 0) {
        // First try exact match
        let question = assessment.assessmentQuestionArray
          .find((q) => q.properties?.dimensionName === chartDimensionName);

        if (question) return chartDimensionName;

        // Try to find by label matching (dynamic approach)
        question = assessment.assessmentQuestionArray
          .find((q) => {
            const dimensionName = q.properties?.dimensionName;
            const dimensionLabel = this.getDimensionLabel(dimensionName);
            return dimensionLabel === chartDimensionName;
          });

        if (question) return question.properties?.dimensionName;
      }

      return chartDimensionName; // Return original if no mapping found
    },

    // Calculate aggregated data for a group of assessments
    calculateAggregatedData(groupedAssessments, dimensionKey) {
      const aggregatedData = [];

      Object.keys(groupedAssessments).sort().forEach((timeKey) => {
        const assessments = groupedAssessments[timeKey];
        if (assessments.length === 0) return;

        // Calculate average value for this time period
        const values = assessments
          .map((assessment) => {
            // Prefer results.dimensions when present (same as bar chart / details).
            // Many types also have assessmentQuestionArray; taking the first matching
            // question per dimension would misalign line series with aggregated means.
            if (this.usesStandardDimensionsStructure(assessment)) {
              const v = assessment.results?.dimensions?.[dimensionKey];
              if (v !== undefined && v !== null) return v;
            }
            if (this.usesQuestionArrayStructure(assessment)) {
              const actualDimensionName = this.mapDimensionName(dimensionKey, assessment);
              return rangeSliderMeanForDimension(
                assessment.assessmentQuestionArray,
                actualDimensionName,
              );
            }
            if (assessment.results?.dimensions?.[dimensionKey] !== undefined) {
              return assessment.results.dimensions[dimensionKey];
            }
            if (assessment.results?.[dimensionKey] !== undefined) {
              return assessment.results[dimensionKey];
            }
            return null;
          })
          .filter((value) => value !== null && value !== undefined);

        if (values.length > 0) {
          const average = values.reduce((sum, val) => sum + val, 0) / values.length;
          const roundedAverage = roundToTwoDecimals(average);

          aggregatedData.push({
            timeKey, // Use original timeKey (ISO timestamp) for matching
            value: roundedAverage,
            count: values.length,
          });
        }
      });

      return aggregatedData;
    },

    calculateDimensionStats(assessments, dimensionKey) {
      const values = assessments
        .map((assessment) => {
          // Handle standard dimensions structure (preferred)
          if (this.usesStandardDimensionsStructure(assessment)) {
            return assessment.results?.dimensions?.[dimensionKey];
          }
          // Handle question array structure (fallback; mirrors backend RangeSliderProcessor)
          if (this.usesQuestionArrayStructure(assessment)) {
            const key = this.mapDimensionName(dimensionKey, assessment);
            return rangeSliderMeanForDimension(assessment.assessmentQuestionArray, key);
          }
          // Handle direct results structure (fallback)
          if (assessment.results) {
            return assessment.results[dimensionKey];
          }
          return undefined;
        })
        .filter((val) => val !== undefined && val !== null);

      if (values.length === 0) {
        return {
          stdDev: 0,
          mean: 0,
        };
      }

      return {
        stdDev: this.calculateStandardDeviation(values),
        mean: values.reduce((sum, val) => sum + val, 0) / values.length,
      };
    },

    changeTab(tabId) {
      this.selectedTab = tabId;
      this.updateChartData(tabId);
    },

    /**
     * Whether chart config is a bar or barWithErrorBars type
     */
    isBarChart(chartConfig) {
      if (!chartConfig) return false;
      const t = chartConfig.type;
      return t === 'bar' || t === 'barWithErrorBars';
    },

    /**
     * Whether chart config is a boxplot
     */
    isBoxplotChart(chartConfig) {
      return chartConfig && chartConfig.type === 'boxplot';
    },

    /**
     * Table rows for boxplot stats (primary / current period dataset).
     */
    getBoxplotStatsRowsForTab(tab, chartCfg) {
      const cfg = chartCfg || tab?.chartConfig;
      if (!this.isBoxplotChart(cfg)) return [];
      const labels = cfg.data?.labels || [];
      const ds = cfg.data?.datasets || [];
      if (!labels.length || !ds.length) return [];
      const id = tab.assessmentId;
      const idx = getBoxplotPrimaryDatasetIndex(
        !!(id && this.selectedComparison1[id]),
        !!(id && this.selectedComparison2[id]),
        ds.length,
      );
      const dataset = ds[idx];
      return buildBoxplotStatsRows(
        labels,
        dataset?.data || [],
        (l) => this.getDimensionLabel(l),
      );
    },

    getBoxplotStatsSeriesCaption(tab, chartCfg) {
      const cfg = chartCfg || tab?.chartConfig;
      if (!this.isBoxplotChart(cfg)) return '';
      const ds = cfg.data?.datasets || [];
      if (ds.length <= 1) return '';
      const id = tab.assessmentId;
      const idx = getBoxplotPrimaryDatasetIndex(
        !!(id && this.selectedComparison1[id]),
        !!(id && this.selectedComparison2[id]),
        ds.length,
      );
      const label = ds[idx]?.label;
      return label
        ? `${this.$t('BoxPlotStatsSection.seriesValuesFor')}: ${label}`
        : '';
    },

    /**
     * Whether chart config is a line chart
     */
    isLineChart(chartConfig) {
      return chartConfig && chartConfig.type === 'line';
    },

    /**
     * External evaluations listed in comparison dropdowns (parent tab vs external).
     * Empty when the active tab is an external evaluation type so only same-type
     * submissions (e.g. different dates) appear — no duplicate groups.
     */
    comparisonExternalEvaluationsForTab(tab) {
      if (this.checkIsExternalEvaluationType(tab.assessmentType)) {
        return [];
      }
      return this.getExternalEvaluationsForType(tab.assessmentType) || [];
    },

    /**
     * Whether comparison controls should appear above this chart (bar/boxplot only).
     * Selection stays per assessment id; duplicate controls stay in sync.
     */
    showComparisonForChart(tab, chartCfg) {
      if (!chartCfg || this.isLineChart(chartCfg)) {
        return false;
      }
      if (!tab.chartConfigs?.length && !tab.chartConfig) {
        return false;
      }
      return (
        this.hasExternalEvaluationsForType(tab.assessmentType) ||
        (this.groupedAssessments[tab.assessmentType]?.length > 1)
      );
    },

    /**
     * Responsive chart height class
     */
    chartHeightClass(chartConfig) {
      if (!chartConfig) return 'h-[300px] lg:h-[400px]';
      return 'h-[300px] lg:h-[400px]';
    },

    /**
     * Color class for the overall average bar
     */
    averageBarColorClass(tab) {
      const avg = this.getOverallAverage(tab);
      if (avg >= 4) return 'bg-green-500';
      if (avg >= 3) return 'bg-orange-400';
      return 'bg-red-400';
    },

    /**
     * Style for the overall average bar width
     */
    averageBarStyle(tab) {
      const avg = this.getOverallAverage(tab);
      return { width: `${(avg / 5) * 100}%` };
    },

    onComparison1Update(tab, v) {
      this.selectedComparison1[tab.assessmentId] = v;
      this.updateChartData(tab.assessmentId);
    },

    onComparison2Update(tab, v) {
      this.selectedComparison2[tab.assessmentId] = v;
      this.updateChartData(tab.assessmentId);
    },

    /**
     * Check if a chart config contains error bar data
     */
    hasErrorBarsInConfig(chartConfig) {
      return chartConfig?.data?.datasets?.some(
        (ds) => ds.data?.some((d) => d !== null && typeof d === 'object' && 'yMin' in d),
      ) || chartConfig?.type === 'barWithErrorBars';
    },

    /**
     * Get current scores map for a tab
     */
    /**
     * Collaboration tracker (KSS) includes kssChampion (non-Likert); omit from score logic.
     */
    dimensionsWithoutKssChampion(dimensions, tab) {
      if (!dimensions || typeof dimensions !== 'object') return dimensions || {};
      if (tab?.assessmentType !== 'kss') return { ...dimensions };
      const copy = { ...dimensions };
      delete copy[KSS_CHAMPION_DIMENSION];
      return copy;
    },

    /**
     * Map a single line dataset label to a dashboard dimension key (same rules as
     * inferDimensionKeysFromLineDatasets).
     */
    resolveDimensionKeyForLineDatasetLabel(tab, lbl, cfg, datasetIndex = -1) {
      if (lbl == null || lbl === '') return null;
      if (
        Array.isArray(cfg?.dimensions)
        && datasetIndex >= 0
        && cfg.dimensions[datasetIndex]
      ) {
        return cfg.dimensions[datasetIndex];
      }
      const locale = this.$i18n?.locale || 'en';
      const dims = this.dimensions;
      const resultsDims = tab?.results?.dimensions;
      let key = null;
      if (dims && dims.length) {
        const byValue = dims.find((d) => d.value === lbl);
        if (byValue) key = byValue.value;
        if (!key) {
          const byTranslation = dims.find(
            (d) => d.translation?.[locale] === lbl
              || d.translation?.en === lbl
              || d.translation?.de === lbl,
          );
          if (byTranslation) key = byTranslation.value;
        }
      }
      if (!key && resultsDims && typeof resultsDims[lbl] === 'number') {
        key = lbl;
      }
      return key;
    },

    /**
     * Map a bar/boxplot category label (often translated) to schema dimension value.
     */
    resolveChartCategoryToDimensionKey(tab, categoryLabel, cfg) {
      if (categoryLabel == null && categoryLabel !== 0) return null;
      const labels = cfg?.data?.labels || [];
      const chartDims = cfg?.dimensions;
      const idx = labels.indexOf(categoryLabel);
      if (idx >= 0 && Array.isArray(chartDims) && chartDims[idx]) {
        return chartDims[idx];
      }
      const dimList = this.dimensions;
      if (dimList?.length) {
        const byVal = dimList.find((d) => d.value === categoryLabel);
        if (byVal) return byVal.value;
        const locale = this.$i18n?.locale || 'en';
        const byTr = dimList.find(
          (d) => d.translation?.[locale] === categoryLabel
            || d.translation?.en === categoryLabel
            || d.translation?.de === categoryLabel,
        );
        if (byTr) return byTr.value;
      }
      const rd = tab?.results?.dimensions;
      if (rd && typeof rd[categoryLabel] === 'number') return categoryLabel;
      return null;
    },

    /**
     * Line charts: infer dimension keys from dataset definitions when `dimensions` is omitted
     * (matches dataset order; resolves translated labels back to dimension values).
     */
    inferDimensionKeysFromLineDatasets(tab, chartCfg) {
      const cfg = chartCfg || tab?.chartConfig || this.lineChartConfigForTab(tab);
      if (!cfg?.data?.datasets?.length) return [];
      const locale = this.$i18n?.locale || 'en';
      const avgLabel = cfg.averageData?.enabled
        ? (locale === 'de' ? cfg.averageData.title?.de : cfg.averageData.title?.en)
        : null;
      const keys = [];
      const seen = new Set();

      cfg.data.datasets.forEach((ds, dsIdx) => {
        const lbl = ds?.label;
        if (lbl == null || lbl === '') return;
        if (avgLabel && (lbl === avgLabel || lbl === 'Average')) return;
        const key = this.resolveDimensionKeyForLineDatasetLabel(tab, lbl, cfg, dsIdx);
        if (key != null && !seen.has(key)) {
          seen.add(key);
          keys.push(key);
        }
      });
      return keys;
    },

    /**
     * Dimension keys for one chart config (building that chart’s datasets / bars).
     * For score summary, change-over-time, and detail views use getAssessmentDetailDimensionKeys.
     * Priority: chartConfig.dimensions > type-specific inference > assessment results.
     * - Line: never use data.labels (those are time buckets). Infer from datasets when needed.
     * - Bar / boxplot / barWithErrorBars: use category labels (excluding average bar).
     */
    getChartDimensionKeys(tab, chartCfg) {
      const cfg = chartCfg || (() => {
        if (tab?.chartConfigs?.length) {
          const line = tab.chartConfigs.find((c) => this.isLineChart(c));
          if (line) return line;
          return tab.chartConfigs[0];
        }
        return tab?.chartConfig;
      })();
      const chartDims = cfg?.dimensions;
      if (chartDims && chartDims.length > 0) {
        return [...chartDims];
      }

      const chartType = cfg?.type;

      if (chartType === 'line') {
        const inferred = this.inferDimensionKeysFromLineDatasets(tab, cfg);
        if (inferred.length > 0) return inferred;
      } else {
        const chartLabels = cfg?.data?.labels;
        if (chartLabels && chartLabels.length > 0) {
          if (cfg.averageData?.enabled) {
            const locale = this.$i18n?.locale || 'en';
            const avgTitle = locale === 'de'
              ? cfg.averageData.title?.de
              : cfg.averageData.title?.en;
            return chartLabels.filter((label) => label !== avgTitle && label !== 'Average');
          }
          return [...chartLabels];
        }
      }

      if (tab?.results?.dimensions) {
        return Object.keys(tab.results.dimensions);
      }
      return [];
    },

    /**
     * Restrict kpiValues to numeric assessment dimensions (same key set as detail / insights).
     */
    filterKpiValuesForChart(tab, kpiValues) {
      if (!kpiValues || typeof kpiValues !== 'object') return {};
      const keys = this.getAssessmentDetailDimensionKeys(tab);
      if (!keys || keys.length === 0) return { ...kpiValues };
      const out = {};
      keys.forEach((k) => {
        if (kpiValues[k] !== undefined && kpiValues[k] !== null) {
          out[k] = kpiValues[k];
        }
      });
      return out;
    },

    /**
     * All numeric Likert dimensions for the tab’s latest assessment (detail, timeline,
     * score summary, change-over-time, strengths/growth). Charts may show a subset only.
     * Order: schema dimensions when available, then any remaining keys.
     */
    getAssessmentDetailDimensionKeys(tab) {
      if (!tab?.results?.dimensions) return [];
      const raw = this.dimensionsWithoutKssChampion(tab.results.dimensions, tab);
      const keys = Object.keys(raw).filter(
        (k) => typeof raw[k] === 'number' && !Number.isNaN(raw[k]),
      );
      const schemaDims = tab.dimensions;
      if (schemaDims?.length) {
        const ordered = [];
        const seen = new Set();
        schemaDims.forEach((d) => {
          const v = d.value;
          if (v != null && keys.includes(v)) {
            ordered.push(v);
            seen.add(v);
          }
        });
        keys.forEach((k) => {
          if (!seen.has(k)) ordered.push(k);
        });
        return ordered;
      }
      return [...keys].sort();
    },

    /**
     * KPI map for Week detail / sticky timeline: every assessment dimension, values from
     * sticky entry or the matching raw assessment.
     */
    buildDetailKpiValues(tab, entry) {
      if (!tab || !entry) return {};
      const raw = entry.kpiValues && typeof entry.kpiValues === 'object'
        ? entry.kpiValues
        : {};
      const keys = this.getAssessmentDetailDimensionKeys(tab);
      if (keys.length === 0) {
        return { ...raw };
      }
      const assessment = this.groupedAssessments[tab.assessmentType]?.find(
        (a) => String(a._id) === String(entry.assessmentId),
      );
      const dims = assessment?.results?.dimensions || {};
      const out = {};
      keys.forEach((k) => {
        if (raw[k] !== undefined && raw[k] !== null) {
          out[k] = raw[k];
        } else if (typeof dims[k] === 'number' && !Number.isNaN(dims[k])) {
          out[k] = dims[k];
        }
      });
      const seen = new Set(Object.keys(out));
      Object.keys(raw).forEach((k) => {
        if (seen.has(k)) return;
        const v = raw[k];
        if (typeof v === 'number' && !Number.isNaN(v)) {
          out[k] = v;
          seen.add(k);
        }
      });
      return out;
    },

    getCurrentScores(tab) {
      if (!tab?.results?.dimensions) return {};
      const allScores = this.dimensionsWithoutKssChampion(tab.results.dimensions, tab);
      const allowedKeys = this.getAssessmentDetailDimensionKeys(tab);
      if (!allowedKeys || allowedKeys.length === 0) return allScores;
      const filtered = {};
      allowedKeys.forEach((key) => {
        if (allScores[key] !== undefined) {
          filtered[key] = allScores[key];
        }
      });
      return filtered;
    },

    /**
     * Get overall average score for a tab
     */
    getOverallAverage(tab) {
      const scores = Object.values(this.getCurrentScores(tab)).filter(
        (v) => typeof v === 'number' && !Number.isNaN(v),
      );
      if (scores.length === 0) return 0;
      return roundToTwoDecimals(scores.reduce((a, b) => a + b, 0) / scores.length);
    },

    /**
     * Get dimension entries (sorted by score desc) for a tab
     */
    getDimensionEntries(tab) {
      const scores = this.getCurrentScores(tab);
      return Object.entries(scores)
        .filter(([, score]) => typeof score === 'number' && !Number.isNaN(score))
        .map(([key, score]) => ({
          key,
          label: this.getDimensionLabel(key),
          score: roundToTwoDecimals(score),
        }))
        .sort((a, b) => b.score - a.score);
    },

    /**
     * Get dimension changes between current and previous assessment
     */
    getDimensionChanges(tab) {
      if (!tab?.assessmentType) return null;
      const assessments = this.groupedAssessments[tab.assessmentType];
      if (!assessments || assessments.length < 2) return null;

      const current = assessments[0];
      const previous = assessments[1];
      if (!current?.results?.dimensions || !previous?.results?.dimensions) return null;

      const curr = this.dimensionsWithoutKssChampion(current.results.dimensions, tab);
      const prev = this.dimensionsWithoutKssChampion(previous.results.dimensions, tab);
      const allowedKeys = this.getAssessmentDetailDimensionKeys(tab);

      return Object.keys(curr)
        .filter((key) => prev[key] !== undefined)
        .filter((key) => allowedKeys.length === 0 || allowedKeys.includes(key))
        .filter((key) => typeof curr[key] === 'number' && !Number.isNaN(curr[key])
          && typeof prev[key] === 'number' && !Number.isNaN(prev[key]))
        .map((key) => {
          const currentScore = curr[key];
          const prevScore = prev[key];
          const absoluteChange = roundToTwoDecimals(currentScore - prevScore);
          let percentChange;
          if (prevScore !== 0) {
            percentChange = roundToTwoDecimals(
              ((currentScore - prevScore) / prevScore) * 100,
            );
          } else {
            percentChange = currentScore > 0 ? 100 : 0;
          }
          return {
            key,
            label: this.getDimensionLabel(key),
            currentScore: roundToTwoDecimals(currentScore),
            previousScore: roundToTwoDecimals(prevScore),
            absoluteChange,
            percentChange,
            improved: absoluteChange > 0,
            unchanged: absoluteChange === 0,
          };
        })
        .sort((a, b) => b.absoluteChange - a.absoluteChange);
    },

    /**
     * Get change summary for a tab
     */
    getChangeSummary(tab) {
      const changes = this.getDimensionChanges(tab);
      if (!changes) return null;
      const improved = changes.filter((d) => d.absoluteChange > 0).length;
      const declined = changes.filter((d) => d.absoluteChange < 0).length;
      const unchanged = changes.filter((d) => d.absoluteChange === 0).length;
      return {

        improved,
        declined,
        unchanged,
        total: changes.length,
      };
    },

    /**
     * Get previous assessment date for a tab
     */
    getPreviousDate(tab) {
      if (!tab?.assessmentType) return '';
      const assessments = this.groupedAssessments[tab.assessmentType];
      if (!assessments || assessments.length < 2) return '';
      return formatLocaleDate(
        assessments[1].date_assessmentMade || assessments[1].submittedAt,
        this.$i18n?.locale,
      );
    },

    /**
     * Get statistical context for a tab
     */
    getStatisticalContext(tab) {
      const scores = Object.values(this.getCurrentScores(tab)).filter(
        (v) => typeof v === 'number' && !Number.isNaN(v),
      );
      // Need ≥2 dimensions to describe spread across dimensions; hide when no / insufficient data
      if (scores.length < 2) {
        return null;
      }
      const mean = scores.reduce((a, b) => a + b, 0) / scores.length;
      const variance = scores.reduce((sum, v) => sum + (v - mean) ** 2, 0) / scores.length;
      const sd = Math.sqrt(variance);
      let variability;
      let messageKey;
      if (sd < 0.5) {
        variability = 'low';
        messageKey = 'lowVariability';
      } else if (sd < 1.0) {
        variability = 'moderate';
        messageKey = 'moderateVariability';
      } else {
        variability = 'high';
        messageKey = 'highVariability';
      }
      return {
        averageSD: sd.toFixed(2),
        variability,
        messageKey,
      };
    },

    /**
     * True if value is a drawable point (bar, line w/ error object, boxplot stats).
     */
    isPlottableChartScalar(value) {
      if (value == null) return false;
      if (typeof value === 'number') return !Number.isNaN(value);
      if (typeof value === 'object') {
        if (typeof value.y === 'number' && !Number.isNaN(value.y)) return true;
        if (typeof value.median === 'number' && !Number.isNaN(value.median)) return true;
        if (typeof value.mean === 'number' && !Number.isNaN(value.mean)) return true;
      }
      return false;
    },

    /**
     * Whether current chart config has at least one numeric datapoint to render.
     */
    chartHasPlottableData(tab, chartCfg) {
      const cfg = chartCfg ?? tab?.chartConfig;
      if (!cfg?.data?.datasets?.length) return false;
      return cfg.data.datasets.some((ds) => {
        const arr = ds.data;
        if (!Array.isArray(arr)) return false;
        return arr.some((point) => this.isPlottableChartScalar(point));
      });
    },

    /**
     * My Results: show chart when there is plottable data only.
     * Schema `minAssessmentsForCharts` applies to Team Results, not here.
     */
    shouldShowChartVisualization(tab, chartCfg) {
      return this.chartHasPlottableData(tab, chartCfg);
    },

    getChartEmptyPlaceholderText() {
      return this.$t('MyResultsDashboardComponent.noAssessmentResults');
    },

    /**
     * Comparison deltas: second dropdown (comp2) minus first (comp1), per dimension.
     * Positive = higher score in "compare to" selection vs first selection.
     * Numeric dimension scores for comparison deltas (dimensions or question array).
     * @param {Object} assessment
     * @returns {Record<string, number>}
     */
    getComparisonScoresRecord(assessment) {
      const out = {};
      if (this.usesStandardDimensionsStructure(assessment)) {
        Object.entries(assessment.results.dimensions || {}).forEach(([k, v]) => {
          if (typeof v === 'number' && !Number.isNaN(v)) out[k] = v;
        });
        return out;
      }
      if (this.usesQuestionArrayStructure(assessment)) {
        return dimensionScoresFromRangeSliderQuestions(assessment.assessmentQuestionArray);
      }
      if (assessment.results && typeof assessment.results === 'object') {
        Object.entries(assessment.results).forEach(([k, v]) => {
          if (typeof v === 'number' && !Number.isNaN(v)) out[k] = v;
        });
      }
      return out;
    },

    getComparisonDeltas(tab) {
      const comp1Id = this.selectedComparison1[tab.assessmentId];
      const comp2Id = this.selectedComparison2[tab.assessmentId];
      if (!comp1Id || !comp2Id) return null;

      const comp1 = this.findComparisonAssessment(comp1Id, tab.assessmentType);
      const comp2 = this.findComparisonAssessment(comp2Id, tab.assessmentType);
      if (!comp1 || !comp2) return null;

      const scores1 = this.getComparisonScoresRecord(comp1);
      const scores2 = this.getComparisonScoresRecord(comp2);

      return Object.keys(scores1)
        .filter((key) => scores2[key] !== undefined && typeof scores2[key] === 'number')
        .map((key) => ({
          key,
          label: this.getDimensionLabel(key),
          value: roundToTwoDecimals(scores2[key] - scores1[key]),
        }));
    },

    async onTimePeriodChange(timePeriod, customDateRange = null) {
      // Handle time period change for line charts
      this.selectedTimePeriod = timePeriod;
      this.customDateRange = customDateRange; // Store custom date range
      if (this.selectedTab) {
        await this.updateChartData(this.selectedTab);
      }
    },

    async onTimeGranularityChange() {
      // Handle time granularity change for line charts
      if (this.selectedTab) {
        await this.updateChartData(this.selectedTab);
      }
    },

    onCustomDateChange() {
      // Auto-apply when both dates are selected
      if (this.customStartDate && this.customEndDate) {
        this.applyCustomRange();
      }
    },

    applyCustomRange() {
      if (this.customStartDate && this.customEndDate) {
        // Validate that start date is before end date
        if (new Date(this.customStartDate) > new Date(this.customEndDate)) {
          this.$toast?.add?.({
            severity: 'warn',
            summary: this.$t('TimePeriodSelector.invalidDateRange') || 'Invalid Date Range',
            detail: this.$t('TimePeriodSelector.startBeforeEnd') || 'Start date must be before end date',
            life: 3000,
          });
          return;
        }

        // Emit custom date range with both dates
        this.onTimePeriodChange('custom', {
          startDate: this.customStartDate,
          endDate: this.customEndDate,
        });
      }
    },

    filterAssessmentsByTimePeriod(assessments) {
      if (this.selectedTimePeriod === 'all') {
        return assessments;
      }

      // Handle custom date range
      if (this.selectedTimePeriod === 'custom' && this.customDateRange) {
        const startDate = new Date(this.customDateRange.startDate);
        const endDate = new Date(this.customDateRange.endDate);

        return assessments.filter((assessment) => {
          const assessmentDate = new Date(assessment.date_assessmentMade ||
            assessment.submittedAt ||
            assessment.date ||
            assessment.createdAt ||
            new Date());
          return assessmentDate >= startDate && assessmentDate <= endDate;
        });
      }

      const now = new Date();
      let cutoffDate;

      switch (this.selectedTimePeriod) {
        case 'week':
          cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'quarter':
          cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case 'year':
          cutoffDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          return assessments;
      }

      return assessments.filter((assessment) => {
        const assessmentDate = new Date(assessment.date_assessmentMade ||
          assessment.submittedAt ||
          assessment.date ||
          assessment.createdAt ||
          new Date());
        return assessmentDate >= cutoffDate;
      });
    },

    createAverageDataset(assessments, chartConfig, groupedAssessments = null) {
      const { averageData } = chartConfig;
      if (!averageData || !averageData.enabled || !averageData.dimensions?.length) {
        return null;
      }

      const {
        dimensions: avgDimensions,
        weights: avgWeights,
        title,
        backgroundColor,
        borderColor,
      } = averageData;
      const locale = this.$i18n?.locale || 'en';
      const label = locale === 'de' ? title.de : title.en;

      // If we have grouped assessments (for time granularity), calculate aggregated averages
      if (groupedAssessments) {
        const data = Object.keys(groupedAssessments).sort().map((timeKey) => {
          const timeAssessments = groupedAssessments[timeKey];
          if (timeAssessments.length === 0) return null;

          let weightedSum = 0;
          let totalWeight = 0;

          avgDimensions.forEach((dim) => {
            const values = timeAssessments
              .map((assessment) => {
                if (this.usesStandardDimensionsStructure(assessment)) {
                  const v = assessment.results?.dimensions?.[dim];
                  if (v !== undefined && v !== null) return v;
                }
                if (this.usesQuestionArrayStructure(assessment)) {
                  const actualDimensionName = this.mapDimensionName(dim, assessment);
                  return rangeSliderMeanForDimension(
                    assessment.assessmentQuestionArray,
                    actualDimensionName,
                  );
                }
                if (assessment.results?.dimensions?.[dim] !== undefined) {
                  return assessment.results.dimensions[dim];
                }
                if (assessment.results?.[dim] !== undefined) {
                  return assessment.results[dim];
                }
                return null;
              })
              .filter((value) => value !== null && value !== undefined);

            if (values.length > 0) {
              const average = values.reduce((sum, val) => sum + val, 0) / values.length;
              const weight = avgWeights[dim] || 1;
              weightedSum += average * weight;
              totalWeight += weight;
            }
          });

          if (totalWeight === 0) return null;

          const average = weightedSum / totalWeight;
          return roundToTwoDecimals(average);
        });

        return {
          label,
          data,
          borderColor: borderColor || '#rgb(255, 107, 107)',
          backgroundColor: backgroundColor || '#rgba(255, 107, 107, 0.5)',
          fill: false,
          tension: 0.1,
        };
      }

      // Fallback to original logic for individual assessments
      const data = assessments.map((assessment) => {
        let weightedSum = 0;
        let totalWeight = 0;

        avgDimensions.forEach((dim) => {
          let value = null;
          if (this.usesStandardDimensionsStructure(assessment)) {
            const v = assessment.results?.dimensions?.[dim];
            if (v !== undefined && v !== null) value = v;
          }
          if (value == null && this.usesQuestionArrayStructure(assessment)) {
            const actualDimensionName = this.mapDimensionName(dim, assessment);
            value = rangeSliderMeanForDimension(
              assessment.assessmentQuestionArray,
              actualDimensionName,
            );
          }
          if (value == null && assessment.results?.dimensions?.[dim] !== undefined) {
            value = assessment.results.dimensions[dim];
          } else if (value == null && assessment.results?.[dim] !== undefined) {
            value = assessment.results[dim];
          }

          if (value !== undefined && value !== null) {
            const weight = avgWeights[dim] || 1;
            weightedSum += value * weight;
            totalWeight += weight;
          }
        });

        if (totalWeight === 0) return null;

        const average = weightedSum / totalWeight;
        const roundedAverage = roundToTwoDecimals(average);

        // Return just the value, not {x, y} object
        return roundedAverage;
      });

      const averageDataset = {
        label,
        data,
        borderColor: borderColor?.startsWith('#') ? borderColor : `#${borderColor}`,
        backgroundColor: backgroundColor?.startsWith('#') ? backgroundColor : `#${backgroundColor}`,
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 2,
        spanGaps: true, // This will connect points across gaps
      };
      return averageDataset;
    },

    /**
     * Create the dimension-average dataset for a bar chart
     * (one value per label, all equal to weighted average).
     * @param {Object} assessment - Single assessment (e.g. current/latest)
     * @param {Object} chartConfig - Chart config with averageData and data.labels
     * @returns {Object|null} Dataset to push or null
     */
    createAverageDatasetForBarChart(assessment, chartConfig) {
      const { averageData } = chartConfig;
      if (!averageData || !averageData.enabled || !averageData.dimensions?.length
        || !chartConfig?.data?.labels) {
        return null;
      }
      const {
        dimensions: avgDimensions,
        weights: avgWeights,
        title,
        backgroundColor,
        borderColor,
      } = averageData;
      const locale = this.$i18n?.locale || 'en';
      const label = locale === 'de' ? title?.de : title?.en;

      let weightedSum = 0;
      let totalWeight = 0;

      avgDimensions.forEach((dim) => {
        let value = null;
        if (this.usesStandardDimensionsStructure(assessment)) {
          const v = assessment.results?.dimensions?.[dim];
          if (v !== undefined && v !== null) value = v;
        }
        if (value == null && this.usesQuestionArrayStructure(assessment)) {
          const actualDimensionName = this.mapDimensionName(dim, assessment);
          value = rangeSliderMeanForDimension(
            assessment.assessmentQuestionArray,
            actualDimensionName,
          );
        }
        if (value == null && assessment.results?.dimensions?.[dim] !== undefined) {
          value = assessment.results.dimensions[dim];
        } else if (value == null && assessment.results?.[dim] !== undefined) {
          value = assessment.results[dim];
        }
        if (value !== undefined && value !== null) {
          const weight = avgWeights[dim] ?? 1;
          weightedSum += value * weight;
          totalWeight += weight;
        }
      });

      if (totalWeight === 0) return null;

      const roundedAverage = roundToTwoDecimals(weightedSum / totalWeight);
      const numLabels = chartConfig.data.labels.length;
      // Display average as a separate bar: null for all dimension bars,
      // value only for the last (Average) bar
      const data = [...Array(numLabels - 1).fill(null), roundedAverage];

      return {
        label: label || 'Average',
        data,
        borderColor: borderColor?.startsWith('#') ? borderColor : `#${borderColor || '999'}`,
        backgroundColor: backgroundColor?.startsWith('#') ? backgroundColor : `#${backgroundColor || '999'}`,
        borderWidth: 2,
      };
    },

    async updateChartData(assessmentId) {
      const tab = this.latestAssessments.find((t) => t.assessmentId === assessmentId);
      if (!tab) {
        return;
      }
      if (!tab.chartConfigs?.length) {
        if (!tab.chartConfig) return;
        tab.chartConfigs = [tab.chartConfig];
      }

      const rawOrig = this.originalChartConfigs[assessmentId];
      const origArray = Array.isArray(rawOrig) ? rawOrig : (rawOrig ? [rawOrig] : []);

      for (let chartIndex = 0; chartIndex < tab.chartConfigs.length; chartIndex += 1) {
        tab.chartConfig = tab.chartConfigs[chartIndex];
        const perOriginal = origArray[chartIndex]
          ? JSON.parse(JSON.stringify(origArray[chartIndex]))
          : JSON.parse(JSON.stringify(tab.chartConfig));
        this.originalChartConfigs[assessmentId] = perOriginal;

        // Handle line chart scenario
        if (tab.chartConfig.type === 'line') {
        // Get all assessments of this type
        let allAssessments = (this.groupedAssessments[tab.assessmentType] || [])
          .slice() // copy
          .sort((a, b) => new Date(a.date_assessmentMade) - new Date(b.date_assessmentMade));

        // Apply time period filtering for line charts
        allAssessments = this.filterAssessmentsByTimePeriod(allAssessments);

        // Only set smart default granularity on initial load, not on user changes
        // This prevents overriding user's manual granularity selection

        // Group assessments by selected time granularity
        const groupedAssessments = this
          .groupAssessmentsByTimePeriod(allAssessments, this.selectedTimeGranularity);

        let labels = [];
        let datasets = [];

        // Create labels from grouped time periods
        labels = Object.keys(groupedAssessments).sort();

        // Same dimension key resolution as insights / score summary (chart config > line inference > fallback)
        const dimensionKeys = this.getChartDimensionKeys(tab, tab.chartConfig);

        // Build a dataset for each dimension using aggregated data
        datasets = dimensionKeys.map((dimensionKey, idx) => {
          // Get translated label for display purposes
          const dimensionLabel = this.getDimensionLabel(dimensionKey);
          // Calculate aggregated data for this dimension
          const aggregatedData = this.calculateAggregatedData(groupedAssessments, dimensionKey);

          // Map aggregated data to chart format
          const data = labels.map((timeKey) => {
            const aggregatedItem = aggregatedData.find((item) => item.timeKey === timeKey);
            return aggregatedItem ? aggregatedItem.value : null;
          });

          return {
            label: dimensionLabel,
            data,
            borderColor:
              tab.chartConfig.data.datasets[idx]?.borderColor
              || `hsl(${idx * 60}, 70%, 50%)`,
            backgroundColor:
              tab.chartConfig.data.datasets[idx]?.backgroundColor
              || `hsl(${idx * 60}, 70%, 80%)`,
            fill: false,
            tension: 0.4,
            spanGaps: true, // This will connect points across gaps
          };
        });

        // Add averaging dataset if enabled
        if (tab.chartConfig.averageData && tab.chartConfig.averageData.enabled) {
          const averageDataset = this.createAverageDataset(
            allAssessments,
            tab.chartConfig,
            groupedAssessments,
          );
          if (averageDataset) {
            datasets.push(averageDataset);
          }
        }

        // Update chart config
        const updatedChartConfig = JSON.parse(JSON.stringify(tab.chartConfig));
        updatedChartConfig.data.labels = labels;
        updatedChartConfig.data.datasets = datasets;

        // Set fixed y-axis scale with min 0 and max 5 for line charts
        if (updatedChartConfig.options && updatedChartConfig.options.scales) {
          if (updatedChartConfig.options.scales.y) {
            updatedChartConfig.options.scales.y.max = 6;
            updatedChartConfig.options.scales.y.min = 0;
          } else {
            updatedChartConfig.options.scales.y = {
              max: 5,
              min: 0,
            };
          }
        } else {
          updatedChartConfig.options = {
            ...updatedChartConfig.options,
            scales: {
              ...updatedChartConfig.options?.scales,
              y: {
                max: 5,
                min: 0,
              },
            },
          };
        }

        tab.chartConfigs[chartIndex] = updatedChartConfig;
        tab.chartConfig = updatedChartConfig;
        continue;
      }

      // Bar chart: optional conversion to boxplot when enough own aggregated results (CMS)
      if (
        tab.chartConfig.type === 'bar'
        && this.shouldRenderBarChartAsBoxplotForOwnResults(tab, chartIndex)
      ) {
        const boxplotConfig = this.buildBoxplotConfigForExternalEval(tab, chartIndex);
        if (boxplotConfig) {
          tab.chartConfigs[chartIndex] = boxplotConfig;
          tab.chartConfig = boxplotConfig;
          continue;
        }
      }

      // Boxplot: only when chart config type is 'boxplot' (from assessment manager)
      if (tab.chartConfig.type === 'boxplot') {
        const boxplotConfig = this.buildBoxplotConfigForExternalEval(tab, chartIndex);
        if (boxplotConfig) {
          tab.chartConfigs[chartIndex] = boxplotConfig;
          tab.chartConfig = boxplotConfig;
          continue;
        }
        // Keep saved boxplot config so the chart still renders; do not fall through to bar path
        continue;
      }

      // Create a copy of the original chart config
      const updatedChartConfig = JSON.parse(JSON.stringify(tab.chartConfig));
      const datasets = [];

      // Only show standard deviation if there are comparisons (exclude Mindset360)
      const hasComparison1 = this.selectedComparison1[assessmentId];
      const hasComparison2 = this.selectedComparison2[assessmentId];
      const hasComparisons = (hasComparison1 || hasComparison2);

      // Use original chart config for latest dataset and config color so they are not lost after comparison runs
      const baseConfig = this.originalChartConfigs[assessmentId]
        ? JSON.parse(JSON.stringify(this.originalChartConfigs[assessmentId]))
        : null;
      const latestDatasetSource = baseConfig?.data?.datasets?.[0] ?? updatedChartConfig.data.datasets[0];

      // Bar chart comparison: left dropdown = grey + left, right dropdown = config color + right
      const barChartConfigColor = (() => {
        const bg = latestDatasetSource?.backgroundColor;
        const bc = latestDatasetSource?.borderColor;
        const color = (typeof bg === 'string' ? bg : bg?.[0]) || (typeof bc === 'string' ? bc : bc?.[0]);
        const normalized = color && (color.startsWith('#') ? color : `#${color}`);
        return normalized || '#f97316';
      })();
      const barChartGreyColor = '#9ca3af';

      const getComparisonDateLabel = (a) => (a?.date_assessmentMade || a?.submittedAt)
        ? formatLocaleDate(a.date_assessmentMade || a.submittedAt, this.$i18n?.locale)
        : '';
      const getComparisonTypeLabel = (comparisonId) => {
        const t = this.getComparisonType(comparisonId);
        return t === 'external' ? `(${this.$t('MyResultsDashboardComponent.external') || 'External'})` : '';
      };

      // Case 1: No comparisons - show latest only with original chart config colors
      if (!hasComparison1 && !hasComparison2) {
        datasets.push({
          ...latestDatasetSource,
          label: this.getAssessmentName(tab.assessmentName),
        });
      }

      // Case 2: Only left dropdown – [left selection (grey), latest (config)]
      if (hasComparison1 && !hasComparison2) {
        const leftAssessment = this.findComparisonAssessment(
          this.selectedComparison1[assessmentId],
          tab.assessmentType,
        );
        if (leftAssessment) {
          const leftData = this.mapAssessmentResultsToChartData(leftAssessment, updatedChartConfig);
          datasets.push({
            data: leftData.data,
            label: `${this.getAssessmentName(tab.assessmentName)} ${getComparisonDateLabel(leftAssessment)} ${getComparisonTypeLabel(this.selectedComparison1[assessmentId])}`,
            backgroundColor: barChartGreyColor,
            borderColor: barChartGreyColor,
            borderWidth: 1,
          });
        }
        datasets.push({
          ...latestDatasetSource,
          label: this.getAssessmentName(tab.assessmentName),
          backgroundColor: barChartConfigColor,
          borderColor: barChartConfigColor,
        });
      }

      // Case 3: Only right dropdown – [latest (grey), right selection (config)]; original config preserved for reset
      if (!hasComparison1 && hasComparison2) {
        datasets.push({
          ...latestDatasetSource,
          label: this.getAssessmentName(tab.assessmentName),
          backgroundColor: barChartGreyColor,
          borderColor: barChartGreyColor,
        });
        const rightAssessment = this.findComparisonAssessment(
          this.selectedComparison2[assessmentId],
          tab.assessmentType,
        );
        if (rightAssessment) {
          const rightData = this.mapAssessmentResultsToChartData(rightAssessment, updatedChartConfig);
          datasets.push({
            data: rightData.data,
            label: `${this.getAssessmentName(tab.assessmentName)} ${getComparisonDateLabel(rightAssessment)} ${getComparisonTypeLabel(this.selectedComparison2[assessmentId])}`,
            backgroundColor: barChartConfigColor,
            borderColor: barChartConfigColor,
            borderWidth: 1,
          });
        }
      }

      // Case 4: Both dropdowns – [left selection (grey), right selection (config)]
      if (hasComparison1 && hasComparison2) {
        const leftAssessment = this.findComparisonAssessment(
          this.selectedComparison1[assessmentId],
          tab.assessmentType,
        );
        const rightAssessment = this.findComparisonAssessment(
          this.selectedComparison2[assessmentId],
          tab.assessmentType,
        );
        if (leftAssessment) {
          const leftData = this.mapAssessmentResultsToChartData(leftAssessment, updatedChartConfig);
          datasets.push({
            data: leftData.data,
            label: `${this.getAssessmentName(tab.assessmentName)} ${getComparisonDateLabel(leftAssessment)} ${getComparisonTypeLabel(this.selectedComparison1[assessmentId])}`,
            backgroundColor: barChartGreyColor,
            borderColor: barChartGreyColor,
            borderWidth: 1,
          });
        }
        if (rightAssessment) {
          const rightData = this.mapAssessmentResultsToChartData(rightAssessment, updatedChartConfig);
          datasets.push({
            data: rightData.data,
            label: `${this.getAssessmentName(tab.assessmentName)} ${getComparisonDateLabel(rightAssessment)} ${getComparisonTypeLabel(this.selectedComparison2[assessmentId])}`,
            backgroundColor: barChartConfigColor,
            borderColor: barChartConfigColor,
            borderWidth: 1,
          });
        }
      }

      // Add dimension average as a separate bar when enabled
      if (tab.chartConfig.averageData?.enabled && tab.chartConfig.averageData?.dimensions?.length) {
        const currentAssessment = this.groupedAssessments[tab.assessmentType]?.[0];
        if (currentAssessment) {
          const avgData = tab.chartConfig.averageData;
          const locale = this.$i18n?.locale || 'en';
          const averageLabel = locale === 'de' ? (avgData.title?.de) : (avgData.title?.en);
          const averageLabelText = averageLabel || 'Average';
          // Add "Average" as a new category so it appears as a separate bar
          updatedChartConfig.data.labels = [
            ...(updatedChartConfig.data.labels || []),
            averageLabelText,
          ];
          // Extend existing datasets with null so they don't show a bar in the Average column
          datasets.forEach((ds) => {
            if (Array.isArray(ds.data)) ds.data.push(null);
          });
          const averageDataset = this.createAverageDatasetForBarChart(
            currentAssessment,
            updatedChartConfig,
          );
          if (averageDataset) {
            datasets.push(averageDataset);
          }
        }
      }

      // Add standard deviation as error bars on the main dataset
      // Uses chartjs-chart-error-bars format: { y, yMin, yMax }
      let assessmentsForStdDev = [];

      if (hasComparisons) {
        if (hasComparison1) {
          const comparison1 = this.findComparisonAssessment(
            this.selectedComparison1[assessmentId],
            tab.assessmentType,
          );
          if (comparison1) assessmentsForStdDev.push(comparison1);
        }

        if (hasComparison2) {
          const comparison2 = this.findComparisonAssessment(
            this.selectedComparison2[assessmentId],
            tab.assessmentType,
          );
          if (comparison2) assessmentsForStdDev.push(comparison2);
        }
      } else {
        const currentAssessment = this.groupedAssessments[tab.assessmentType]?.[0];
        assessmentsForStdDev = currentAssessment ? [currentAssessment] : [];
      }

      // Show standard deviation for external evaluation types
      if (this.checkIsExternalEvaluationType(tab.assessmentType)) {
        const chartLabels = updatedChartConfig.data.labels;
        const hasAverageBar = tab.chartConfig.averageData?.enabled
          && tab.chartConfig.averageData?.dimensions?.length
          && chartLabels.length > 0;
        let sdValues = [];

        if (assessmentsForStdDev.length > 1) {
          let dimensionKeys = [];
          if (assessmentsForStdDev.length > 0) {
            const firstAssessment = assessmentsForStdDev[0];
            if (this.usesStandardDimensionsStructure(firstAssessment)) {
              dimensionKeys = Object.keys(firstAssessment.results.dimensions);
            } else if (this.usesQuestionArrayStructure(firstAssessment)) {
              dimensionKeys = firstAssessment.assessmentQuestionArray
                .map((q) => q.properties?.dimensionName)
                .filter((name) => name);
            }
          }

          sdValues = chartLabels.map((label, index) => {
            if (hasAverageBar && index === chartLabels.length - 1) return 0;
            const dimensionKey = dimensionKeys.find((key) => {
              if (this.usesStandardDimensionsStructure(assessmentsForStdDev[0])) {
                return key === label;
              }
              if (this.usesQuestionArrayStructure(assessmentsForStdDev[0])) return key === label;
              return false;
            });
            if (dimensionKey) {
              const stats = this.calculateDimensionStats(assessmentsForStdDev, dimensionKey);
              return stats.stdDev;
            }
            return 0;
          });
        } else if (assessmentsForStdDev.length === 1) {
          const singleAssessment = assessmentsForStdDev[0];
          // Legacy: SD from task event dimensionData (old backend)
          const dimensionData = singleAssessment?.originalTaskData
            ?.taskEvents[0]?.data?.dimensionData;
          if (dimensionData && dimensionData.length > 0) {
            sdValues = chartLabels.map((label, index) => {
              if (hasAverageBar && index === chartLabels.length - 1) return 0;
              const dimensionItem = dimensionData
                .find((d) => d.dimensionName === label ||
                d.dimensionValue === label ||
                d.name === label ||
                this.getDimensionLabel(d.dimensionValue) === label);
              return dimensionItem ? dimensionItem.std : 0;
            });
          } else {
            // New own-results format: SD from results.dimensions_stdDev (content-management API)
            const dimensionsStdDev = singleAssessment?.results?.dimensions_stdDev;
            if (dimensionsStdDev && typeof dimensionsStdDev === 'object') {
              sdValues = chartLabels.map((label, index) => {
                if (hasAverageBar && index === chartLabels.length - 1) return 0;
                // Match by dimension key or by translated label
                const sd = dimensionsStdDev[label];
                if (typeof sd === 'number') return sd;
                const dimensionKey = Object.keys(dimensionsStdDev).find(
                  (key) => this.getDimensionLabel(key) === label,
                );
                return dimensionKey != null && typeof dimensionsStdDev[dimensionKey] === 'number'
                  ? dimensionsStdDev[dimensionKey]
                  : 0;
              });
            } else {
              sdValues = new Array(chartLabels.length).fill(0);
            }
          }
        } else {
          sdValues = new Array(chartLabels.length).fill(0);
        }

        // Convert the first (main) dataset to error bar format: { y, yMin, yMax }
        // This renders proper T-shaped whiskers centered on each bar
        if (datasets.length > 0 && sdValues.some((v) => v > 0)) {
          const mainDataset = datasets[0];
          mainDataset.data = mainDataset.data.map((value, index) => {
            const sd = sdValues[index] || 0;
            if (value === null || value === undefined) return value;
            const numVal = typeof value === 'object' ? value.y : Number(value);
            if (sd > 0) {
              return {
                y: numVal,
                yMin: Math.max(0, numVal - sd),
                yMax: Math.min(5, numVal + sd),
              };
            }
            return { y: numVal, yMin: numVal, yMax: numVal };
          });
          // Switch chart type to barWithErrorBars
          mainDataset.type = 'barWithErrorBars';
          // Style the error bar whiskers
          mainDataset.errorBarLineWidth = 2;
          mainDataset.errorBarWhiskerLineWidth = 2;
          mainDataset.errorBarWhiskerSize = 0.4;
          mainDataset.errorBarColor = '#333333';
          mainDataset.errorBarWhiskerColor = '#333333';
        }
      }

      // Update the chart config with new datasets
      updatedChartConfig.data.datasets = datasets;

      // Set fixed y-axis scale with min 0 and max 5 for all bar charts
      if (this.isBarChart(tab.chartConfig)) {
        if (updatedChartConfig.options && updatedChartConfig.options.scales) {
          if (updatedChartConfig.options.scales.y) {
            updatedChartConfig.options.scales.y.max = 6;
            updatedChartConfig.options.scales.y.min = 0;
          } else {
            updatedChartConfig.options.scales.y = {
              max: 6,
              min: 0,
            };
          }
        } else {
          updatedChartConfig.options = {
            ...updatedChartConfig.options,
            scales: {
              ...updatedChartConfig.options?.scales,
              y: {
                max: 6,
                min: 0,
              },
            },
          };
        }
        if (updatedChartConfig.options.scales.x) {
          updatedChartConfig.options.scales.x.stacked = !hasComparisons;
        } else {
          updatedChartConfig.options.scales.x = {
            stacked: !hasComparisons,
          };
        }
      }

      tab.chartConfigs[chartIndex] = updatedChartConfig;
      tab.chartConfig = updatedChartConfig;
      }

      this.originalChartConfigs[assessmentId] = origArray.length
        ? origArray
        : this.originalChartConfigs[assessmentId];
      if (tab.chartConfigs?.length) {
        tab.chartConfig = tab.chartConfigs[0];
      }
    },

    mapAssessmentToChartData(assessment, chartConfig) {
      // Use the new helper method for consistency
      return this.mapAssessmentResultsToChartData(assessment, chartConfig);
    },

    /**
     * Maps assessment results to chart data using dimension values
     * This is the key method that bridges the gap between
     * assessment results (using dimension values)
     * and chart labels (using dimension values)
     */
    /**
     * Resolve chart label index for a dimension key (direct key, translated label, or chart order).
     */
    findChartLabelIndexForDimensionKey(dimensionKey, chartConfig) {
      const labels = chartConfig?.data?.labels;
      if (!labels || !dimensionKey) return -1;
      let idx = labels.indexOf(dimensionKey);
      if (idx !== -1) return idx;
      const translated = this.getDimensionLabel(dimensionKey);
      idx = labels.indexOf(translated);
      if (idx !== -1) return idx;
      return labels.findIndex((lbl) => this.getDimensionLabel(dimensionKey) === lbl);
    },

    mapAssessmentResultsToChartData(assessment, chartConfig) {
      // Check if chart config has data and labels
      if (!chartConfig || !chartConfig.data || !chartConfig.data.labels) {
        return { data: [] };
      }

      const labels = chartConfig.data.labels;
      const chartDims = chartConfig.dimensions;
      const n = labels.length;
      // null = no value for this chart category (avoids fake 0 / template placeholders)
      const data = new Array(n).fill(null);

      const setAt = (index, value) => {
        if (index >= 0 && index < n && typeof value === 'number' && !Number.isNaN(value)) {
          data[index] = roundToTwoDecimals(value);
        }
      };

      // 1) Align by chartConfig.dimensions[] when parallel to labels (authoritative order)
      if (Array.isArray(chartDims)) {
        for (let i = 0; i < n; i += 1) {
          const dimKey = chartDims[i];
          if (dimKey == null || dimKey === '') continue;
          if (this.usesStandardDimensionsStructure(assessment)) {
            const v = assessment.results.dimensions[dimKey];
            if (typeof v === 'number' && !Number.isNaN(v)) setAt(i, v);
          } else if (this.usesQuestionArrayStructure(assessment)) {
            const mapped = this.mapDimensionName(dimKey, assessment);
            const v = rangeSliderMeanForDimension(assessment.assessmentQuestionArray, mapped);
            if (typeof v === 'number' && !Number.isNaN(v)) setAt(i, v);
          }
        }
      }

      // 2) Match each result key to a label (raw key, translated title, or index)
      if (this.usesStandardDimensionsStructure(assessment)) {
        Object.entries(assessment.results.dimensions).forEach(([dimensionValue, value]) => {
          if (typeof value !== 'number' || Number.isNaN(value)) return;
          let labelIndex = this.findChartLabelIndexForDimensionKey(dimensionValue, chartConfig);
          if (labelIndex === -1 && Array.isArray(chartDims)) {
            labelIndex = chartDims.indexOf(dimensionValue);
          }
          if (labelIndex !== -1 && data[labelIndex] == null) {
            data[labelIndex] = roundToTwoDecimals(value);
          }
        });
      } else if (this.usesQuestionArrayStructure(assessment)) {
        const scores = dimensionScoresFromRangeSliderQuestions(assessment.assessmentQuestionArray);
        Object.entries(scores).forEach(([dimensionValue, value]) => {
          if (typeof value !== 'number' || Number.isNaN(value)) return;
          let labelIndex = this.findChartLabelIndexForDimensionKey(dimensionValue, chartConfig);
          if (labelIndex === -1 && Array.isArray(chartDims)) {
            labelIndex = chartDims.indexOf(dimensionValue);
          }
          if (labelIndex !== -1 && data[labelIndex] == null) {
            data[labelIndex] = roundToTwoDecimals(value);
          }
        });
      } else if (assessment.results) {
        Object.entries(assessment.results).forEach(([dimensionValue, value]) => {
          if (typeof value !== 'number') return;
          let labelIndex = this.findChartLabelIndexForDimensionKey(dimensionValue, chartConfig);
          if (labelIndex === -1 && Array.isArray(chartDims)) {
            labelIndex = chartDims.indexOf(dimensionValue);
          }
          if (labelIndex !== -1 && data[labelIndex] == null) {
            data[labelIndex] = roundToTwoDecimals(value);
          }
        });
      }

      return { data };
    },

    /**
     * Get translated dimension label for display purposes
     * @param {string} dimensionValue - The dimension value
     * @returns {string} The translated label
     */
    getDimensionLabel(dimensionValue) {
      // Special case: kss_avg is calculated, not a real dimension in the database
      if (dimensionValue === 'kss_avg') {
        const locale = this.$i18n?.locale || 'en';
        return locale === 'de' ? 'Durchschnittlicher Wissensaustausch' : 'Average KSS';
      }

      const dimension = this.dimensions.find((d) => d.value === dimensionValue);
      const locale = this.$i18n?.locale || 'en';
      return dimension?.translation?.[locale] || dimensionValue;
    },

    /**
     * Determine if an assessment uses question array structure (like KSS)
     * @param {Object} assessment - The assessment object
     * @returns {boolean} True if uses question array structure
     */
    usesQuestionArrayStructure(assessment) {
      return assessment.assessmentQuestionArray &&
             assessment.assessmentQuestionArray.length > 0 &&
             assessment.assessmentQuestionArray.some((q) => q.properties?.dimensionName);
    },

    /**
     * Determine if an assessment uses standard results.dimensions structure
     * @param {Object} assessment - The assessment object
     * @returns {boolean} True if uses standard dimensions structure
     */
    usesStandardDimensionsStructure(assessment) {
      return assessment.results &&
             assessment.results.dimensions &&
             Object.keys(assessment.results.dimensions).length > 0;
    },

    /**
     * Determine if an assessment should have fixed y-axis scale (0-6)
     * @param {Object} assessment - The assessment object or tab object
     * @returns {boolean} True if should have fixed scale
     */
    shouldHaveFixedYAxisScale(assessment) {
      // For processed tab objects, check if they have question array structure
      if (assessment.assessmentType) {
        // This is a processed tab object, check the original assessment type
        const originalAssessment = this.groupedAssessments[assessment.assessmentType]?.[0];
        if (originalAssessment) {
          return !this.usesQuestionArrayStructure(originalAssessment);
        }
        // Fallback: assume standard structure if we can't find original
        return true;
      }

      // For raw assessment objects, use question array structure as indicator
      return !this.usesQuestionArrayStructure(assessment);
    },

    /**
     * Get translated assessment name for display purposes
     * @param {Object} assessmentName - The assessment name object with en/de properties
     * @returns {string} The translated assessment name
     */
    getAssessmentName(assessmentName) {
      const locale = this.$i18n?.locale || 'en';
      return assessmentName?.[locale] || assessmentName?.en || '';
    },

    /**
     * Find comparison assessment by ID, handling cross-type comparisons
     * @param {string} comparisonId - The comparison ID (may be prefixed with type)
     * @param {string} currentType - The current assessment type
     * @returns {Object|null} The found assessment or null
     */
    findComparisonAssessment(comparisonId, currentType) {
      if (!comparisonId) return null;

      // Handle cross-type comparisons (external evaluations)
      if (comparisonId.startsWith('external-')) {
        const actualId = comparisonId.replace('external-', '');
        // Search through all external evaluation types
        const externalEvaluations = this.getExternalEvaluationsForType(currentType);
        const found = externalEvaluations.find((a) => a._id === actualId);
        if (found) return found;

        // Fallback: search all external evaluation assessments
        // eslint-disable-next-line no-restricted-syntax
        for (const [, assessments] of Object.entries(this.externalEvaluationAssessments)) {
          const assessment = assessments?.find((a) => a._id === actualId);
          if (assessment) return assessment;
        }
        return null;
      }

      // Handle same-type comparisons
      return this.groupedAssessments[currentType]?.find((a) => a._id === comparisonId);
    },

    /**
     * Get comparison type from comparison ID
     * @param {string} comparisonId - The comparison ID
     * @returns {string} The comparison type ('external', 'self', or 'same')
     */
    getComparisonType(comparisonId) {
      if (!comparisonId) return 'same';
      if (comparisonId.startsWith('external')) return 'external';
      return 'same';
    },

    /**
     * Get the linked parent assessment type for an external evaluation type
     * Uses schema-based lookup via externalEvaluationSchemaId
     * @param {string} externalEvalType - The external evaluation type
     * @returns {string|null} The parent assessment type or null
     */
    getLinkedParentType(externalEvalType) {
      // Use the getter from assessmentSchemas store
      return this.getParentTypeForExternalEvaluation(externalEvalType);
    },

    /**
     * Get external evaluations that can be compared with a given assessment type
     * @param {string} assessmentType - The assessment type to find external evaluations for
     * @returns {Array} Array of external evaluation assessments
     */
    getExternalEvaluationsForType(assessmentType) {
      // Use schema-based lookup to find linked external evaluation type
      const externalType = this.getExternalEvaluationTypeForParent(assessmentType);
      if (externalType && this.groupedAssessments[externalType]) {
        return this.groupedAssessments[externalType];
      }

      // Fallback: check if any external evaluation has matching dimensions
      // eslint-disable-next-line no-restricted-syntax
      for (const [assessments] of Object.entries(this.externalEvaluationAssessments)) {
        if (assessments?.length > 0) {
          const selfAssessments = this.groupedAssessments[assessmentType];
          if (selfAssessments?.length > 0) {
            const selfDimensions = this.getAssessmentDimensions(selfAssessments[0]);
            const externalDimensions = this.getAssessmentDimensions(assessments[0]);
            if (this.dimensionsMatch(selfDimensions, externalDimensions)) {
              return assessments;
            }
          }
        }
      }
      return [];
    },

    /**
     * Check if an assessment type has linked external evaluations available
     * @param {string} assessmentType - The assessment type to check
     * @returns {boolean} True if external evaluations are available
     */
    hasExternalEvaluationsForType(assessmentType) {
      return this.getExternalEvaluationsForType(assessmentType).length > 0;
    },

    /**
     * Check if an assessment type is an external evaluation type
     * @param {string} assessmentType - The assessment type to check
     * @returns {boolean} True if it's an external evaluation type
     */
    checkIsExternalEvaluationType(assessmentType) {
      // Use the getter from assessmentSchemas store
      return this.isExternalEvaluationType(assessmentType);
    },

    /**
     * Get dimensions from an assessment
     * @param {Object} assessment - The assessment object
     * @returns {Array} Array of dimension values
     */
    getAssessmentDimensions(assessment) {
      if (this.usesStandardDimensionsStructure(assessment)) {
        return Object.keys(assessment.results.dimensions);
      }
      if (this.usesQuestionArrayStructure(assessment)) {
        return assessment.assessmentQuestionArray
          .map((q) => q.properties?.dimensionName)
          .filter((name) => name);
      }
      if (assessment.results) {
        return Object.keys(assessment.results).filter((key) => typeof assessment.results[key] === 'number');
      }
      return [];
    },

    /**
     * Check if two dimension arrays match
     * @param {Array} dimensions1 - First dimension array
     * @param {Array} dimensions2 - Second dimension array
     * @returns {boolean} True if dimensions match
     */
    dimensionsMatch(dimensions1, dimensions2) {
      if (!dimensions1 || !dimensions2) {
        return false;
      }

      const sorted1 = [...dimensions1].sort();
      const sorted2 = [...dimensions2].sort();

      return sorted1.some((dim) => sorted2.includes(dim));
    },

    async processAssessmentData() {
      const { assessments: userAssessments } = this;
      const { assessmentSchemas: schemas } = this;
      const { charts } = this;

      // Get latest assessment for each type
      // Sticky questions are excluded from chart data; they are displayed
      // separately via the StickyQuestionResults component.
      this.groupedAssessments = userAssessments
        .sort((a, b) => new Date(b.date_assessmentMade) - new Date(a.date_assessmentMade))
        .reduce((acc, assessment) => {
          const { type } = assessment;
          if (!acc[type]) {
            acc[type] = [];
          }
          if (!acc[type].some((a) => a._id === assessment._id)) {
            const chartAssessment = {
              ...assessment,
              assessmentQuestionArray: (assessment.assessmentQuestionArray || [])
                .filter((q) => !q.sticky),
            };
            acc[type].push(chartAssessment);
          }
          return acc;
        }, {});

      // Match assessments with their schemas and charts (own + both visibility)
      const latestAssessments = Object.entries(this.groupedAssessments)
        .map(([type, assessments]) => {
          const assessment = assessments[0];
          const schema = schemas.find((s) => s.type === type);
          if (!schema) {
            return null;
          }

          const schemaCharts = charts.filter(
            (c) => c.assessmentId === schema._id
              && ['own', 'both'].includes(c.visibility || 'both'),
          );

          if (
            !schemaCharts.length
            || (!this.usesStandardDimensionsStructure(assessment)
              && !this.usesQuestionArrayStructure(assessment))
          ) {
            return null;
          }

          const chartConfigs = [];
          const originals = [];

          schemaCharts.forEach((chartConfig) => {
            const updatedChartConfig = JSON.parse(JSON.stringify(chartConfig));
            const mappedData = this.mapAssessmentResultsToChartData(assessment, updatedChartConfig);
            if (updatedChartConfig.data?.datasets?.[0]) {
              updatedChartConfig.data.datasets[0].data = mappedData.data;
            }
            originals.push(JSON.parse(JSON.stringify(updatedChartConfig)));
            chartConfigs.push(updatedChartConfig);
          });

          this.originalChartConfigs[assessment._id] = originals;

          return {
            assessmentId: assessment._id,
            assessmentType: type,
            assessmentName: schema.representationProperties?.assessmentName,
            submittedAt: assessment.date_assessmentMade,
            results: assessment.results || {},
            dimensions: schema.dimensions,
            chartConfigs,
            chartConfig: chartConfigs[0],
            generatedFromSchema: assessment.generatedFromSchema,
          };
        })
        .filter(Boolean);

      return latestAssessments;
    },

    onChartLabelHover(label) {
      this.hoveredChartLabel = label;
    },

    onChartLabelLeave() {
      this.hoveredChartLabel = null;
    },

    onChartLabelClick(label) {
      if (this.selectedTimeBucket === label) {
        this.selectedTimeBucket = null;
        this.selectedAssessmentId = null;
        this.chartSelectedAssessmentId = null;
      } else {
        this.selectedTimeBucket = label;
        const match = this.stickyResultsData.find((entry) => {
          if (!entry.completedAt) return false;
          return this.entryMatchesDateLabel(entry, label);
        });
        const matchedId = match?.assessmentId || null;
        this.selectedAssessmentId = matchedId;
        this.chartSelectedAssessmentId = matchedId;
      }
    },

    onTimelineSelectEntry({ assessmentId, dateKey }) {
      this.selectedAssessmentId = assessmentId;
      this.selectedTimeBucket = dateKey;
    },

    dateToChartLabelKey(date) {
      const granularity = this.selectedTimeGranularity;
      if (granularity === 'weekly') {
        const d = new Date(date);
        const dayOfWeek = d.getDay();
        const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        d.setDate(d.getDate() + daysToMonday);
        d.setHours(0, 0, 0, 0);
        return d.toISOString().split('T')[0];
      }
      if (granularity === 'monthly') {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
      }
      return date.toISOString().split('T')[0];
    },

    entryMatchesDateLabel(entry, label) {
      if (!entry.completedAt) return false;
      const entryDate = new Date(entry.completedAt).toISOString().split('T')[0];
      if (entryDate === label) return true;
      if (label.endsWith('-01') && label.length === 10) {
        return entryDate.substring(0, 7) === label.substring(0, 7);
      }
      const labelDate = new Date(label);
      const entryDateObj = new Date(entry.completedAt);
      return this.getISOWeek(entryDateObj) === this.getISOWeek(labelDate);
    },

    getISOWeek(date) {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const weekNum = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
      return `${d.getUTCFullYear()}-W${weekNum}`;
    },

    handleWindowResize() {
      this.windowWidth = window.innerWidth;
    },

    onStickyDataLoaded(results) {
      this.stickyResultsData = results || [];
      if (!this.chartSelectedAssessmentId && results.length > 0) {
        this.chartSelectedAssessmentId = results[0].assessmentId;
      }
      if (!this.selectedAssessmentId && results.length > 0) {
        this.selectedAssessmentId = results[0].assessmentId;
        this.selectedTimeBucket = new Date(results[0].completedAt)
          .toISOString().split('T')[0];
      }
    },

    resetComparison(assessmentId) {
      this.selectedComparison1[assessmentId] = '';
      this.selectedComparison2[assessmentId] = '';
      // Find the current tab and refresh its chart config with latest assessment data
      const tab = this.latestAssessments.find((t) => t.assessmentId === assessmentId);
      if (tab) {
        // Get the latest assessment for this type
        const latestAssessment = this.groupedAssessments[tab.assessmentType]?.[0];
        if (latestAssessment) {
          // Refresh the chart config with the latest assessment data
          const updatedChartConfig = JSON.parse(JSON.stringify(tab.chartConfig));
          if (this.isBarChart(updatedChartConfig)) {
            const mappedData = this.mapAssessmentResultsToChartData(
              latestAssessment,
              updatedChartConfig,
            );
            updatedChartConfig.data.datasets[0].data = mappedData.data;
          }
          tab.chartConfig = updatedChartConfig;
        }
      }

      // Update chart data to recalculate standard deviation with all assessments
      this.updateChartData(assessmentId);
    },

    /**
     * TODO: REFACTOR - This method uses old backend API and should be migrated to new backend
     * WARNING: This is a temporary solution for KSS champion data
     * Fetches KSS history data from the old backend API using existing NetworkContent method
     * Now respects the user's selected time period
     */
    async fetchKssHistory() {
      if (!this.isCurrentTabKSS) return;

      try {
        this.kssHistoryLoading = true;
        this.kssHistoryError = null;

        // Calculate date range based on selected time period
        const dateRange = this.calculateDateRangeForKssHistory();

        // TODO: REFACTOR - This uses old backend API and should be migrated to new backend
        // WARNING: This endpoint should be migrated to the new backend architecture
        // Now passes date parameters to the old backend API
        const { data } = await NetworkContent.getKssHistory(null, dateRange);

        this.kssHistoryData = data.data;
      } catch (error) {
        console.error('Failed to fetch KSS history:', error);
        this.kssHistoryError = 'Failed to load KSS champion statistics';
      } finally {
        this.kssHistoryLoading = false;
      }
    },

    /**
     * Calculate date range based on selected time period for KSS history
     * TODO: This will be used when backend supports date parameters
     */
    calculateDateRangeForKssHistory() {
      const now = new Date();
      let start;
      let end;

      if (this.selectedTimePeriod === 'custom' && this.customDateRange) {
        start = new Date(this.customDateRange.startDate);
        end = new Date(this.customDateRange.endDate);
      } else {
        switch (this.selectedTimePeriod) {
          case 'week':
            start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            end = now;
            break;
          case 'month':
            start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            end = now;
            break;
          case 'quarter':
            start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            end = now;
            break;
          case 'year':
            start = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
            end = now;
            break;
          case 'all':
          default:
            // For 'all' time, we could set a very early date or handle differently
            start = new Date('2020-01-01'); // Arbitrary early date
            end = now;
            break;
        }
      }

      return { start, end };
    },

    /**
     * Get the appropriate title for KSS history based on selected time period
     */
    getKssHistoryTitle() {
      switch (this.selectedTimePeriod) {
        case 'week':
          return this.$t('DashboardKssHistoryComponent.kssChampionDataTitleWeek') || 'Your knowledge exchange in the last week';
        case 'month':
          return this.$t('DashboardKssHistoryComponent.kssChampionDataTitleMonth') || 'Your knowledge exchange in the last month';
        case 'quarter':
          return this.$t('DashboardKssHistoryComponent.kssChampionDataTitleQuarter') || 'Your knowledge exchange in the last quarter';
        case 'year':
          return this.$t('DashboardKssHistoryComponent.kssChampionDataTitleYear') || 'Your knowledge exchange in the last year';
        case 'custom':
          return this.$t('DashboardKssHistoryComponent.kssChampionDataTitleCustom') || 'Your knowledge exchange in the selected period';
        case 'all':
        default:
          return this.$t('DashboardKssHistoryComponent.kssChampionDataTitle') || 'Your knowledge exchange over time';
      }
    },

    /**
     * Fetch KSS meetings for the KSS Trend Analysis dropdown
     * Called when KSS tab is selected
     */
    async fetchKssMeetings() {
      if (!this.isCurrentTabKSS) return;

      try {
        this.kssTrendLoading = true;
        this.kssTrendError = null;

        const { data } = await NetworkContent.getKssEvalutionPerMeeting();
        if (data.success && data.data) {
          this.kssMeetings = data.data;
          const filtered = this.filteredKssMeetings;
          if (filtered.length > 0 && !this.selectedKssMeeting) {
            // eslint-disable-next-line prefer-destructuring
            this.selectedKssMeeting = filtered[0];
            await this.onKssMeetingSelect(this.selectedKssMeeting);
          }
        } else {
          this.kssMeetings = [];
        }
      } catch (error) {
        console.error('Failed to fetch KSS meetings:', error);
        this.kssTrendError = this.$t('MyResultsDashboardComponent.failedToLoadMeetings')
          || 'Failed to load KSS meetings';
      } finally {
        this.kssTrendLoading = false;
      }
    },

    /**
     * Handle native select change for KSS meeting dropdown - resolve meeting from seriesId and load plot
     */
    onKssMeetingChange(event) {
      const seriesId = event?.target?.value;
      if (!seriesId) return;
      const meeting = this.filteredKssMeetings.find((m) => m.seriesId === seriesId)
        || this.kssMeetings.find((m) => m.seriesId === seriesId);
      if (meeting) this.onKssMeetingSelect(meeting);
    },

    /**
     * Handle KSS meeting selection - fetch assessment data and generate chart
     * Chart generation moved to frontend for consistency with other assessment charts
     * @param {Object} meeting - The selected meeting object
     */
    async onKssMeetingSelect(meeting) {
      if (!meeting || !meeting.seriesId) return;

      try {
        this.kssTrendLoading = true;
        this.kssTrendError = null;
        this.selectedKssMeeting = meeting;
        this.kssRawOccurrences = null;
        this.kssRawAssessments = null;
        this.kssOccurrenceCustomStart = '';
        this.kssOccurrenceCustomEnd = '';

        const { data } = await NetworkContent.getKssEvalutionPerMeetingPlotData({
          seriesId: meeting.seriesId,
          language: this.$i18n?.locale || 'en',
        });

        if (data.success && data.data) {
          const responseData = data.data;

          if (!responseData.hasEnoughData) {
            this.kssTrendBarConfig = null;
            this.kssTrendBoxplotConfig = null;
            this.kssTrendPlotData = null;
            this.kssTrendError = this.$t('DashboardComponent.notEnoughDataAvailable')
              || 'Not enough data available';
          } else if (responseData.isRecurring && responseData.occurrences) {
            this.kssRawOccurrences = responseData.occurrences;
            this.rebuildKssChartFromOccurrences();
          } else if (responseData.assessments) {
            this.kssRawAssessments = responseData.assessments;
            const configs = this.generateKssTrendChartConfigs(responseData.assessments);
            this.kssTrendBarConfig = configs.bar;
            this.kssTrendBoxplotConfig = configs.boxplot;
            this.kssTrendPlotData = this.kssShowBoxplot && configs.boxplot
              ? configs.boxplot : configs.bar;
          } else {
            this.kssTrendBarConfig = null;
            this.kssTrendBoxplotConfig = null;
            this.kssTrendPlotData = responseData;
          }
        } else {
          this.kssTrendBarConfig = null;
          this.kssTrendBoxplotConfig = null;
          this.kssTrendPlotData = null;
        }
      } catch (error) {
        console.error('Failed to fetch KSS plot data:', error);
        this.kssTrendError = this.$t('MyResultsDashboardComponent.failedToLoadPlotData')
          || 'Failed to load chart data';
        this.kssTrendPlotData = null;
      } finally {
        this.kssTrendLoading = false;
      }
    },

    filterByTimePeriod(items, timePeriod, customStart, customEnd, dateField, fallbackDateField) {
      if (!items || items.length === 0) return items;
      if (timePeriod === 'all') return items;

      let startDate;
      let endDate;

      if (timePeriod === 'custom') {
        if (!customStart || !customEnd) return items;
        startDate = new Date(customStart);
        endDate = new Date(customEnd);
        endDate.setHours(23, 59, 59, 999);
      } else {
        const now = new Date();
        endDate = now;
        const offsets = { week: 7, month: 30, quarter: 90, year: 365 };
        const days = offsets[timePeriod];
        if (!days) return items;
        startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      }

      return items.filter((item) => {
        const d = new Date(item[dateField] || item[fallbackDateField] || 0);
        return d >= startDate && d <= endDate;
      });
    },

    onKssMeetingTimePeriodChange(value) {
      this.kssMeetingTimePeriod = value;
      if (value !== 'custom') {
        this.kssMeetingCustomStart = '';
        this.kssMeetingCustomEnd = '';
      }
      const filtered = this.filteredKssMeetings;
      if (this.selectedKssMeeting
        && !filtered.find((m) => m.seriesId === this.selectedKssMeeting.seriesId)) {
        if (filtered.length > 0) {
          this.onKssMeetingSelect(filtered[0]);
        } else {
          this.selectedKssMeeting = null;
          this.kssTrendPlotData = null;
          this.kssTrendBarConfig = null;
          this.kssTrendBoxplotConfig = null;
          this.kssRawOccurrences = null;
          this.kssRawAssessments = null;
        }
      }
    },

    applyKssMeetingCustomRange() {
      if (!this.kssMeetingCustomStart || !this.kssMeetingCustomEnd) return;
      if (new Date(this.kssMeetingCustomStart) > new Date(this.kssMeetingCustomEnd)) {
        alert(this.$t('TimePeriodSelector.invalidDateRange') || 'Start date must be before end date');
        return;
      }
      this.onKssMeetingTimePeriodChange('custom');
    },

    onKssOccurrenceDateChange() {
      const { min, max } = this.kssOccurrenceDateRange;
      if (this.kssOccurrenceCustomStart) {
        if (this.kssOccurrenceCustomStart < min) this.kssOccurrenceCustomStart = min;
        if (this.kssOccurrenceCustomStart > max) this.kssOccurrenceCustomStart = max;
      }
      if (this.kssOccurrenceCustomEnd) {
        if (this.kssOccurrenceCustomEnd < min) this.kssOccurrenceCustomEnd = min;
        if (this.kssOccurrenceCustomEnd > max) this.kssOccurrenceCustomEnd = max;
      }
      if (this.kssOccurrenceCustomStart && this.kssOccurrenceCustomEnd
        && this.kssOccurrenceCustomStart > this.kssOccurrenceCustomEnd) {
        this.kssOccurrenceCustomEnd = this.kssOccurrenceCustomStart;
      }
      this.rebuildKssChartFromOccurrences();
    },

    resetKssOccurrenceFilter() {
      this.kssOccurrenceCustomStart = '';
      this.kssOccurrenceCustomEnd = '';
      this.rebuildKssChartFromOccurrences();
    },

    onKssToggleBoxplot(val) {
      this.kssShowBoxplot = val;
      if (this.kssShowBoxplot && this.kssTrendBoxplotConfig) {
        this.kssTrendPlotData = this.kssTrendBoxplotConfig;
      } else if (this.kssTrendBarConfig) {
        this.kssTrendPlotData = this.kssTrendBarConfig;
      }
    },

    rebuildKssChartFromOccurrences() {
      if (!this.kssRawOccurrences) return;

      let filtered = this.kssRawOccurrences;

      if (this.kssOccurrenceCustomStart || this.kssOccurrenceCustomEnd) {
        const start = this.kssOccurrenceCustomStart
          ? new Date(this.kssOccurrenceCustomStart + 'T00:00:00') : null;
        const end = this.kssOccurrenceCustomEnd
          ? new Date(this.kssOccurrenceCustomEnd + 'T23:59:59') : null;

        filtered = filtered.filter((occ) => {
          const d = new Date(occ.date);
          if (start && d < start) return false;
          if (end && d > end) return false;
          return true;
        });
      }

      if (filtered.length === 0) {
        this.kssTrendBarConfig = null;
        this.kssTrendBoxplotConfig = null;
        this.kssTrendPlotData = null;
        return;
      }

      const configs = this.generateKssRecurringTrendChartConfigs(filtered);
      this.kssTrendBarConfig = configs.bar;
      this.kssTrendBoxplotConfig = configs.boxplot;
      this.kssTrendPlotData = this.kssShowBoxplot && configs.boxplot
        ? configs.boxplot : configs.bar;
    },

    getAssessmentDimensionValue(assessment, key) {
      if (assessment.results?.dimensions?.[key] !== undefined) {
        return assessment.results.dimensions[key];
      }
      if (assessment.assessmentQuestionArray) {
        const question = assessment.assessmentQuestionArray
          .find((q) => q.properties?.dimensionName === key);
        return question?.answeredValue;
      }
      return undefined;
    },

    extractDimensionKeys(assessment) {
      const excludeChampion = (key) => key && key !== 'kssChampion';
      if (assessment.results?.dimensions) {
        return Object.keys(assessment.results.dimensions).filter(excludeChampion);
      }
      if (assessment.assessmentQuestionArray?.length > 0) {
        return assessment.assessmentQuestionArray
          .map((q) => q.properties?.dimensionName)
          .filter(excludeChampion);
      }
      return [];
    },

    computeQuartiles(values) {
      if (!values || values.length === 0) return null;
      const sorted = [...values].sort((a, b) => a - b);
      const n = sorted.length;
      const lerp = (arr, i) => {
        const lo = Math.floor(i);
        const hi = Math.min(lo + 1, n - 1);
        const t = i - lo;
        return arr[lo] * (1 - t) + arr[hi] * t;
      };
      return {
        min: sorted[0],
        q1: lerp(sorted, (n - 1) * 0.25),
        median: lerp(sorted, (n - 1) * 0.5),
        q3: lerp(sorted, (n - 1) * 0.75),
        max: sorted[n - 1],
      };
    },

    collectDimensionValues(assessments, key) {
      return assessments
        .map((a) => this.getAssessmentDimensionValue(a, key))
        .filter((v) => v !== undefined && v !== null);
    },

    kssYScaleOptions() {
      return { min: 0, suggestedMax: 5, beginAtZero: true, ticks: { precision: 0 } };
    },

    /**
     * Generate both bar and boxplot configs for a single (non-recurring) meeting.
     * Returns { bar, boxplot } -- boxplot is null when n < 2.
     */
    generateKssTrendChartConfigs(assessments) {
      if (!assessments || assessments.length === 0) return { bar: null, boxplot: null };

      const dimensionKeys = this.extractDimensionKeys(assessments[0]);
      if (dimensionKeys.length === 0) return { bar: null, boxplot: null };

      const labelTitle = this.featureName;

      const barData = dimensionKeys.map((key) => {
        const values = this.collectDimensionValues(assessments, key);
        return values.length > 0
          ? roundToTwoDecimals(values.reduce((s, v) => s + v, 0) / values.length)
          : 0;
      });

      const bar = {
        type: 'bar',
        data: {
          labels: dimensionKeys,
          datasets: [{
            label: labelTitle,
            data: barData,
            backgroundColor: '#e4821a',
            borderColor: '#e4821a',
          }],
        },
        options: {
          scales: { y: this.kssYScaleOptions(), x: { ticks: { maxRotation: 45 } } },
          maintainAspectRatio: false,
          responsive: true,
          plugins: { legend: { display: true, position: 'top' } },
        },
      };

      let boxplot = null;
      if (assessments.length >= 2) {
        const boxData = dimensionKeys.map((key) => this.collectDimensionValues(assessments, key));

        boxplot = {
          type: 'boxplot',
          data: {
            labels: dimensionKeys,
            datasets: [{
              label: labelTitle,
              data: boxData,
              backgroundColor: 'rgba(228, 130, 26, 0.5)',
              borderColor: '#000000',
              borderWidth: 2,
            }],
          },
          options: {
            scales: { y: this.kssYScaleOptions() },
            maintainAspectRatio: false,
            responsive: true,
            plugins: { legend: { display: true, position: 'top' } },
          },
        };
      }

      return { bar, boxplot };
    },

    /**
     * Generate both bar and boxplot configs for a recurring meeting series.
     * Returns { bar, boxplot } -- boxplot is null when no occurrence has n >= 2.
     */
    generateKssRecurringTrendChartConfigs(occurrences) {
      if (!occurrences || occurrences.length === 0) return { bar: null, boxplot: null };

      const eligible = occurrences.filter((o) => o.assessments?.length > 0);
      if (eligible.length === 0) return { bar: null, boxplot: null };

      let dimensionKeys = [];
      for (const occ of eligible) {
        dimensionKeys = this.extractDimensionKeys(occ.assessments[0]);
        if (dimensionKeys.length > 0) break;
      }
      if (dimensionKeys.length === 0) return { bar: null, boxplot: null };

      const locale = this.$i18n?.locale || 'en';
      const labels = eligible.map((occ) => {
        const d = new Date(occ.date);
        return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
      });

      const dimensionColors = [
        '#e4821a', '#2196F3', '#4CAF50', '#9C27B0',
        '#FF5722', '#00BCD4', '#FFC107', '#795548',
      ];

      const barDatasets = dimensionKeys.map((key, dimIdx) => {
        const color = dimensionColors[dimIdx % dimensionColors.length];
        const data = eligible.map((occ) => {
          const values = this.collectDimensionValues(occ.assessments, key);
          return values.length > 0
            ? roundToTwoDecimals(values.reduce((s, v) => s + v, 0) / values.length)
            : 0;
        });
        return {
          label: this.getDimensionLabel(key),
          data,
          backgroundColor: color,
          borderColor: color,
        };
      });

      const bar = {
        type: 'bar',
        data: { labels, datasets: barDatasets },
        options: {
          scales: { y: this.kssYScaleOptions(), x: { ticks: { maxRotation: 45 } } },
          maintainAspectRatio: false,
          responsive: true,
          plugins: { legend: { display: true, position: 'top' } },
        },
      };

      const hasMultiData = eligible.some((occ) => occ.assessments.length >= 2);
      let boxplot = null;

      if (hasMultiData) {
        const boxDatasets = dimensionKeys.map((key, dimIdx) => {
          const color = dimensionColors[dimIdx % dimensionColors.length];
          const data = eligible.map((occ) => this.collectDimensionValues(occ.assessments, key));

          return {
            label: this.getDimensionLabel(key),
            data,
            backgroundColor: `${color}80`,
            borderColor: '#000000',
            borderWidth: 2,
          };
        });

        boxplot = {
          type: 'boxplot',
          data: { labels, datasets: boxDatasets },
          options: {
            scales: { y: this.kssYScaleOptions() },
            maintainAspectRatio: false,
            responsive: true,
            plugins: { legend: { display: true, position: 'top' } },
          },
        };
      }

      return { bar, boxplot };
    },

    /**
     * Format date for display in KSS meeting dropdown
     * @param {string|Date} date - The date to format
     * @returns {string} Formatted date string
     */
    formatKssMeetingDate(date) {
      if (!date) return '';
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour12: false,
      };
      const locale = resolveLocaleForDateFormat(this.$i18n?.locale || 'en');
      return new Date(date).toLocaleDateString(locale, options);
    },
  },
  watch: {
    /**
     * Watch for tab changes and fetch KSS history + KSS meetings when switching to KSS tab
     */
    selectedTab: {
      handler(newTab) {
        if (newTab && this.isCurrentTabKSS) {
          this.fetchKssHistory();
          this.fetchKssMeetings();
        }
      },
      immediate: true,
    },

    /**
     * Watch for time period changes and refetch KSS history if on KSS tab
     */
    selectedTimePeriod: {
      handler() {
        if (this.isCurrentTabKSS) {
          this.fetchKssHistory();
        }
      },
    },

    /**
     * Watch for custom date range changes and refetch KSS history if on KSS tab
     */
    customDateRange: {
      handler() {
        if (this.isCurrentTabKSS) {
          this.fetchKssHistory();
        }
      },
      deep: true,
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleWindowResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
  },
  async created() {
    try {
      this.loading = true;

      await this.getUserAssessmentsResultsForDashboard({ filter: { status: 'submitted' } });
      await this.getUserExternalEvaluationResults({ filter: { status: 'submitted' } });
      await this.fetchAssessments();
      await this.fetchCharts();
      await this.fetchDimensions();

      // Process the data after all API calls are complete
      const processedAssessments = await this.processAssessmentData();

      // Update the store with processed data
      this.$store.commit('assessments/SET_ASSESSMENTS', processedAssessments);

      // Set initial selected tab if we have assessments
      // Prefer tab from query (e.g. after completing an assessment) so the matching chart opens
      await this.$nextTick(); // Wait for computed properties to update
      if (this.latestAssessments.length > 0) {
        const tabFromQuery = this.tab ?? this.$route?.query?.tab ?? null;
        const matchingTab = tabFromQuery
          ? this.latestAssessments.find(
              (t) => t.assessmentId === tabFromQuery
                || t.assessmentType === tabFromQuery
                || String(t.assessmentId) === String(tabFromQuery),
            )
          : null;
        this.selectedTab = matchingTab ? matchingTab.assessmentId : this.latestAssessments[0].assessmentId;

        // Update chart data for the initial tab (especially important for KSS charts)
        await this.$nextTick();
        await this.updateChartData(this.selectedTab);
      }

      this.loading = false;
    } catch (error) {
      this.error = 'Failed to load assessment results';
      this.loading = false;
    }
  },
};
</script>

<style scoped>
/* PrimeVue Tabs override for orange accent */
:deep(.p-tabs .p-tab.p-tab-active) {
  color: var(--color-orange, #e4821a);
  border-color: var(--color-orange, #e4821a);
}

:deep(.p-tabs .p-tablist-active-bar) {
  background: var(--color-orange, #e4821a);
}
</style>
